import React, { useState, useRef, useEffect, useCallback } from 'react'
import { AnimatePresence, motion, useScroll, useTransform, useSpring, useInView, useMotionValue, useMotionTemplate, animate } from 'framer-motion'
import {
  ChevronDown, X, Menu, Mountain, MountainSnow, Tent, Snowflake, Wind, Compass,
  Map, BookOpen, Phone, Clock, ArrowUpRight, BrainCircuit, Mic, Volume2, Languages,
  PhoneCall, CloudRain, Settings, Bot,
} from 'lucide-react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import RadialOrbitalTimeline from '../components/ui/radial-orbital-timeline.tsx'
import { CanvasRevealEffect } from '../components/ui/canvas-reveal-effect.tsx'
import { CoolThemeToggle } from '../components/ui/CoolThemeToggle.tsx'
import { ThreeDImageRing } from '../components/ui/three-d-image-ring.tsx'
import { Helmet } from 'react-helmet-async';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      mesh: any
      icosahedronGeometry: any
      meshStandardMaterial: any
      pointLight: any
      ambientLight: any
      directionalLight: any
      group: any
      sphereGeometry: any
      meshBasicMaterial: any
      fog: any
    }
  }
  namespace React {
    namespace JSX {
      interface IntrinsicElements {
        mesh: any
        icosahedronGeometry: any
        meshStandardMaterial: any
        pointLight: any
        ambientLight: any
        directionalLight: any
        group: any
        sphereGeometry: any
        meshBasicMaterial: any
        fog: any
      }
    }
  }
}

// ── Design tokens ─────────────────────────────────────────────

export const BG_URL = '/download.png'

export const FONT_DISPLAY = "'Bebas Neue', sans-serif"
export const FONT_BODY = "'DM Sans', sans-serif"
export const FONT_MONO = "'Space Mono', monospace"

export const WHITE = 'var(--white, #ffffff)'
export const STEEL = 'var(--steel, #6b7a80)'
export const DARK = 'var(--dark, #080b0c)'
export const DEEP = 'var(--deep, #101826)'
export const ACCENT = 'var(--accent, #3a6478)'
export const ACCENT_ICE = 'var(--accent-ice, #c8e6ff)'

export const EASE_OUT = [0.16, 1, 0.3, 1] as const
export const HOVER_FLAME = 'linear-gradient(135deg, #ff4d3b 0%, #ff7a3d 50%, #d43a7a 100%)'

const NETWORK_SERVICES = [
  {
    id: 1,
    title: 'Platform',
    date: 'CORE',
    content:
      'The central intelligence engine orchestrating all enterprise AI operations and data flow. Unified architecture, real-time processing, and global orchestration.',
    category: 'Core',
    icon: BrainCircuit,
    relatedIds: [2, 3, 4, 5, 6, 7, 8],
    status: 'completed',
    energy: 100,
  },
  {
    id: 2,
    title: 'Voice AI',
    date: 'SERVICE',
    content:
      'Speech recognition and synthesis powering natural voice interactions. Real-time speech recognition, voice commands, and audio processing.',
    category: 'Interaction',
    icon: Mic,
    relatedIds: [1, 3, 8],
    status: 'in-progress',
    energy: 85,
  },
  {
    id: 3,
    title: 'TTS',
    date: 'SERVICE',
    content:
      'Neural voice synthesis with human-like prosody across 40+ languages. Neural voice generation, voice cloning, and multilingual audio.',
    category: 'Interaction',
    icon: Volume2,
    relatedIds: [1, 2, 4],
    status: 'in-progress',
    energy: 70,
  },
  {
    id: 4,
    title: 'Translation',
    date: 'SERVICE',
    content:
      'Real-time multilingual translation across 100+ language pairs. Live translation, document translation, and localization.',
    category: 'Processing',
    icon: Languages,
    relatedIds: [1, 3, 5],
    status: 'completed',
    energy: 90,
  },
  {
    id: 5,
    title: 'Call Bots',
    date: 'AGENT',
    content:
      'Autonomous agents handling customer support, lead qualification, and scheduling. Outbound calls, inbound support, and appointment scheduling.',
    category: 'Agents',
    icon: PhoneCall,
    relatedIds: [1, 4, 6],
    status: 'in-progress',
    energy: 75,
  },
  {
    id: 6,
    title: 'Weather',
    date: 'DATA',
    content:
      'Real-time environmental data, alerts, forecasts, and predictive weather insights. Forecast APIs, alerts, and climate data.',
    category: 'Data',
    icon: CloudRain,
    relatedIds: [1, 5, 7],
    status: 'pending',
    energy: 40,
  },
  {
    id: 7,
    title: 'Automation',
    date: 'WORKFLOW',
    content:
      'Automate repetitive workflows, approvals, reporting, and enterprise operations using AI. Workflow automation, email processing, and reporting.',
    category: 'Processing',
    icon: Settings,
    relatedIds: [1, 6, 8],
    status: 'completed',
    energy: 95,
  },
  {
    id: 8,
    title: 'AI Agents',
    date: 'AGENT',
    content:
      'Intelligent agents capable of executing complex business tasks without constant human intervention. Autonomous execution, tool calling, and multi-agent workflows.',
    category: 'Agents',
    icon: Bot,
    relatedIds: [1, 2, 7],
    status: 'in-progress',
    energy: 80,
  },
]

// ── Logo ──────────────────────────────────────────────────────

export function SummitLogo({ fill, size = 32 }: Readonly<{ fill?: string; size?: number }>) {
  return (
    <img
      src="/logo_without_bg.png"
      alt="Gliedai Logo"
      width={size}
      height={size}
      style={{ objectFit: 'contain' }}
    />
  )
}

export const SUMMIT_NAV = [
  { label: 'Home', target: 'hero', isPage: true, url: '/' },
  { label: 'Services', target: 'services', isPage: true, url: '/services' },
  { label: 'Solutions', target: 'ecosystem' },
  { label: 'About', target: 'industries' },
]

export function SummitCta() {
  const isServices = window.location.pathname === '/services'
  return (
    <motion.button
      type="button"
      onClick={() => {
        if (window.location.pathname !== '/') {
          window.location.href = '/#contact'
        } else {
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
        }
      }}
      initial="rest" animate="rest" whileHover="hover" whileTap={{ scale: 0.96 }}
      variants={{ rest: { scale: 1 }, hover: { scale: 1.04 } }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'relative',
        overflow: 'hidden',
        fontFamily: FONT_MONO,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase' as const,
        color: WHITE,
        background: isServices ? 'rgba(255, 255, 255, 0.08)' : DARK,
        border: isServices ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
        padding: '10px 24px',
        cursor: 'pointer',
        clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)'
      }}
    >
      <motion.span variants={{ rest: { x: '-101%' }, hover: { x: '0%' } }} transition={{ duration: 0.45, ease: [0.65, 0, 0.35, 1] }} style={{ position: 'absolute', inset: 0, background: HOVER_FLAME, zIndex: 0, pointerEvents: 'none' }} />
      <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        Contact Us
        <motion.span variants={{ rest: { x: -4, opacity: 0, width: 0 }, hover: { x: 0, opacity: 1, width: 'auto' } }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: 0.1 }} style={{ display: 'inline-block', lineHeight: 0, overflow: 'hidden' }}>→</motion.span>
      </span>
    </motion.button>
  )
}

export function NavbarSummit() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [hide, setHide] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      const isScrolled = window.scrollY > 8
      const isHidden = window.scrollY > 400
      setScrolled(isScrolled)
      setHide(isHidden)
      if (isHidden) {
        setMobileOpen(false)
      }
    }
    const onResize = () => setIsMobile(window.innerWidth < 1024)
    onResize()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize) }
  }, [])

  const lit = scrolled || mobileOpen
  
  const navBg = lit
    ? 'var(--nav-bg-active)'
    : 'var(--nav-bg)'

  const navBorder = lit
    ? 'var(--nav-border-active)'
    : 'var(--nav-border)'

  const navText = 'var(--nav-text)'
  const navTextHover = 'var(--nav-text-hover)'
  const logoText = 'var(--logo-text)'

  const blur = lit
    ? 'blur(14px)'
    : 'blur(8px)'

  const handleNavClick = (item: typeof SUMMIT_NAV[number]) => {
    setMobileOpen(false)
    if (item.isPage) {
      if (window.location.pathname === item.url) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.location.href = item.url || '/'
      }
    } else {
      if (window.location.pathname !== '/') {
        window.location.href = `/#${item.target}`
      } else {
        document.getElementById(item.target)?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/'

  const NavItem = ({
    href,
    label,
    active,
    onClick,
  }: {
    href: string
    label: string
    active: boolean
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  }) => {
    const [hovered, setHovered] = useState(false)
    const activeColor = 'var(--nav-text-hover)'
    const inactiveColor = 'var(--nav-text)'
    const hoverColor = 'var(--nav-text-hover)'

    return (
      <a
        href={href}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '0 14px',
          height: 72,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: active ? 600 : 400,
          color: active ? activeColor : (hovered ? hoverColor : inactiveColor),
          fontFamily: FONT_BODY,
          whiteSpace: 'nowrap',
          letterSpacing: '0.04em',
          textDecoration: 'none',
          transition: 'color 0.25s ease',
          position: 'relative',
        }}
      >
        {label}

        <span
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 20,
            width: active ? 18 : 0,
            height: 2,
            transform: 'translateX(-50%)',
            background: '#38bdf8',
            boxShadow: active
              ? '0 0 8px rgba(56,189,248,0.45)'
              : 'none',
            transition: 'width 0.25s ease, box-shadow 0.25s ease',
          }}
        />
      </a>
    )
  }

  return (
    <div data-summit-nav="" style={{ fontFamily: FONT_BODY }}>
      <motion.nav
        animate={{ y: hide ? -76 : 0, opacity: hide ? 0 : 1 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 72,
          background: navBg,
          backdropFilter: blur,
          WebkitBackdropFilter: blur,
          borderBottom: `1px solid ${navBorder}`,
          boxShadow: 'var(--nav-shadow)',
          zIndex: 400,
          transition: 'background 0.3s ease, border-color 0.3s ease',
        }}
      >
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', height: '100%', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, textDecoration: 'none', flexShrink: 0, marginRight: 8 }}>
            <SummitLogo fill={logoText} size={isMobile ? 22 : 26} />
            <span style={{ fontFamily: FONT_DISPLAY, fontSize: isMobile ? 18 : 22, color: logoText, letterSpacing: '0.12em' }}>GLIEDAI</span>
          </a>

          {/* Desktop nav links */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
              {SUMMIT_NAV.map((item) => {
                const isActive = item.isPage 
                  ? currentPath === item.url 
                  : false
                const href = item.isPage 
                  ? (item.url || '/') 
                  : `/#${item.target}`

                return (
                  <NavItem
                    key={item.label}
                    href={href}
                    label={item.label}
                    active={isActive}
                    onClick={(e) => {
                      e.preventDefault()
                      handleNavClick(item)
                    }}
                  />
                )
              })}
              <div style={{ marginLeft: 12, marginRight: 12, display: 'flex', alignItems: 'center' }}>
                <CoolThemeToggle size="sm" />
              </div>
            </div>
          )}

          {/* Desktop CTA */}
          {!isMobile && (
            <div style={{ flexShrink: 0 }}><SummitCta /></div>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <AnimatePresence mode="wait" initial={false}>
                {mobileOpen
                  ? <motion.button type="button" key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }} onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: logoText, padding: 4, display: 'flex' }}><X size={22} strokeWidth={1.75} /></motion.button>
                  : <motion.button type="button" key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }} onClick={() => setMobileOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: logoText, padding: 4, display: 'flex' }}><Menu size={22} strokeWidth={1.75} /></motion.button>
                }
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.25, ease: 'easeOut' }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--gl-bg)', zIndex: 490, display: 'flex', flexDirection: 'column', fontFamily: FONT_BODY }}>
            <div style={{ height: 72, padding: '0 24px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid var(--border-color)` }}>
              <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                <SummitLogo fill={logoText} size={26} />
                <span style={{ fontFamily: FONT_DISPLAY, fontSize: 22, color: logoText, letterSpacing: '0.12em' }}>GLIEDAI</span>
              </a>
              <motion.button type="button" whileTap={{ scale: 0.9 }} onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: logoText, padding: 4, display: 'flex' }}><X size={22} strokeWidth={1.75} /></motion.button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {SUMMIT_NAV.map((item) => {
                const isActive = item.isPage 
                  ? currentPath === item.url 
                  : false
                return (
                  <div key={item.label} style={{ borderBottom: `1px solid var(--border-color)` }}>
                    <div onClick={() => handleNavClick(item)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', cursor: 'pointer' }}>
                      <span style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: isActive ? 600 : 400, color: isActive ? '#38bdf8' : logoText, letterSpacing: '0.06em' }}>{item.label.toUpperCase()}</span>
                    </div>
                  </div>
                )
              })}
              <div style={{ borderBottom: `1px solid var(--border-color)`, padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: FONT_DISPLAY, fontSize: 22, color: logoText, letterSpacing: '0.06em' }}>THEME</span>
                <CoolThemeToggle size="sm" />
              </div>
            </div>
            <div style={{ padding: 24, flexShrink: 0, borderTop: `1px solid var(--border-color)` }}>
              <motion.button type="button" onClick={() => { setMobileOpen(false); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }) }} whileTap={{ scale: 0.97 }} whileHover="hover" initial="rest" animate="rest" variants={{ rest: {}, hover: {} }} style={{ position: 'relative', overflow: 'hidden', width: '100%', background: 'var(--gl-text)', color: 'var(--gl-bg)', fontSize: 12, fontWeight: 700, padding: '16px 0', border: 'none', cursor: 'pointer', fontFamily: FONT_MONO, letterSpacing: '0.14em', textTransform: 'uppercase' as const, clipPath: 'polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%)' }}>
                <motion.span variants={{ rest: { x: '-101%' }, hover: { x: '0%' } }} transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }} style={{ position: 'absolute', inset: 0, background: HOVER_FLAME, pointerEvents: 'none' }} />
                <span style={{ position: 'relative', zIndex: 1 }}>Contact Us</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Count-up ──────────────────────────────────────────────────

function Counter({ to, delay = 0, duration = 1.6 }: Readonly<{ to: number; delay?: number; duration?: number }>) {
  const [n, setN] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px' })
  useEffect(() => {
    if (!inView) return
    let rafId = 0
    const startMs = performance.now()
    const tick = (now: number) => {
      const elapsed = now - startMs - delay * 1000
      if (elapsed < 0) { rafId = requestAnimationFrame(tick); return }
      const t = Math.min(elapsed / (duration * 1000), 1)
      setN(Math.round(to * (1 - Math.pow(1 - t, 4))))
      if (t < 1) rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [inView, to, delay, duration])
  return <span ref={ref}>{n.toLocaleString('en-US')}</span>
}

// ── Split-text letter reveal ──────────────────────────────────

function SplitText({ text, delay = 0, perChar = 0.018, duration = 0.6, style }: Readonly<{ text: string; delay?: number; perChar?: number; duration?: number; style?: React.CSSProperties }>) {
  return (
    <span style={{ display: 'inline-flex', overflow: 'hidden', ...style }}>
      {text.split('').map((char, i) => (
        <motion.span key={i} initial={{ y: '110%', opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration, delay: delay + i * perChar, ease: [0.22, 1, 0.36, 1] }} style={{ display: 'inline-block', whiteSpace: 'pre' }}>
          {char === ' ' ? ' ' : char}
        </motion.span>
      ))}
    </span>
  )
}

// ── Hero CTA button ───────────────────────────────────────────

function SummitHeroButton({ variant, delay, isMobile, label, initialX }: Readonly<{ variant: 'primary' | 'secondary'; delay: number; isMobile: boolean; label: string; initialX: number }>) {
  const isPrimary = variant === 'primary'
  return (
    <motion.button
      type="button"
      initial="rest" animate="enter" whileHover="hover" whileTap={{ scale: 0.97 }}
      variants={{
        rest: { opacity: 0, x: initialX, y: isMobile ? 12 : 0, scale: 0.96 },
        enter: { opacity: 1, x: 0, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.34, 1.35, 0.64, 1] } },
        hover: { scale: 1.03, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } },
      }}
      style={{ position: 'relative', overflow: 'hidden', fontFamily: FONT_MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: isPrimary ? WHITE : DARK, background: isPrimary ? DARK : 'transparent', border: isPrimary ? 'none' : '1px solid rgba(8,11,12,0.35)', padding: isMobile ? '15px 32px' : '16px 36px', cursor: 'pointer', clipPath: 'polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%)', width: isMobile ? '100%' : undefined, maxWidth: isMobile ? 320 : undefined, pointerEvents: 'auto' }}
    >
      <motion.span variants={{ rest: { x: '-101%' }, enter: { x: '-101%' }, hover: { x: '0%' } }} transition={{ duration: 0.45, ease: [0.65, 0, 0.35, 1] }} style={{ position: 'absolute', inset: 0, background: HOVER_FLAME, zIndex: 0, pointerEvents: 'none' }} />
      <motion.span variants={{ rest: { color: isPrimary ? WHITE : DARK }, enter: { color: isPrimary ? WHITE : DARK }, hover: { color: WHITE } }} transition={{ duration: 0.3, ease: 'easeOut', delay: 0.08 }} style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {label}
        <motion.span variants={{ rest: { x: -6, opacity: 0 }, enter: { x: -6, opacity: 0 }, hover: { x: 0, opacity: 1 } }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: 0.18 }} style={{ display: 'inline-block', lineHeight: 0 }}>→</motion.span>
      </motion.span>
    </motion.button>
  )
}

// ── Hero ──────────────────────────────────────────────────────

function SummitTextFlip({
  words,
}: {
  words: string[]
}) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex(prev => (prev + 1) % words.length)
    }, 4800)

    return () => window.clearInterval(timer)
  }, [words.length])

  const longestWord = words.reduce(
    (longest, current) =>
      current.length > longest.length
        ? current
        : longest,
    ''
  )

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-grid',
        overflow: 'hidden',
        verticalAlign: 'bottom',
        height: '1em',
        minWidth: 'min(100%, 12em)',
      }}
    >
      {/* Keeps layout width stable */}
      <span
        aria-hidden="true"
        style={{
          gridArea: '1 / 1',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          fontFamily: FONT_DISPLAY,
          fontSize: 'inherit',
          lineHeight: 0.9,
          letterSpacing: 'inherit',
        }}
      >
        {longestWord}
      </span>

      <AnimatePresence mode="wait">
        <motion.span
          key={words[index]}
          initial={{
            opacity: 0,
            y: '18%',
          }}
          animate={{
            opacity: 1,
            y: '0%',
          }}
          exit={{
            opacity: 0,
            y: '-18%',
          }}
          transition={{
            duration: 1.0,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{
            gridArea: '1 / 1',
            alignSelf: 'center',
            fontFamily: FONT_DISPLAY,
            fontSize: 'inherit',
            lineHeight: 0.9,
            letterSpacing: 'inherit',
            whiteSpace: 'nowrap',
            color: 'transparent',
            WebkitTextStroke: `2px ${DARK}`,
            textAlign: 'center',
          }}
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

function HeroSummit() {
  const heroWords = [
    'INTELLIGENT WORK',
    'SMART COMMUNICATION',
    'CONNECTED OPERATIONS',
    'AUTONOMOUS WORKFLOWS',
  ]
  const sectionRef = useRef<HTMLDivElement>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth
      setIsMobile(w < 768)
      setIsTablet(w >= 768 && w < 1024)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => {
    if (isMobile) return

    const handleMouseMove = (event: MouseEvent) => {
      if (sectionRef.current) {
        const { left, top } = sectionRef.current.getBoundingClientRect()
        mouseX.set(event.clientX - left)
        mouseY.set(event.clientY - top)
      }
    }

    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isMobile, mouseX, mouseY])

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] })

  const bgY = useTransform(
    scrollYProgress,
    [0, 1],
    ['0%', '-6%']
  )

  const smoothBgY = useSpring(bgY, {
    stiffness: 60,
    damping: 20
  })

  const totalBgY = smoothBgY
  const textY = useTransform(scrollYProgress, [0, 1], ['0px', '220px'])
  const smoothTextY = useSpring(textY, { stiffness: 60, damping: 20 })

  const radialBg = useMotionTemplate`radial-gradient(
    350px circle at ${mouseX}px ${mouseY}px,
    rgba(56, 189, 248, 0.15) 0%,
    rgba(96, 165, 250, 0.08) 25%,
    rgba(139, 92, 246, 0.03) 50%,
    transparent 80%
  )`

  const radialMask = useMotionTemplate`radial-gradient(
    280px circle at ${mouseX}px ${mouseY}px,
    black 20%,
    transparent 100%
  )`

  return (
    <section
      ref={sectionRef}
      style={{
        position: 'relative',
        minHeight: '100svh',
        height: '100svh',
        overflow: 'hidden',
        background: 'transparent',
        zIndex: 200,
        pointerEvents: 'none',
      }}
    >
      {/* Aceternity Card Spotlight Background */}
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {/* Color glow */}
          <motion.div
            style={{
              position: 'absolute',
              inset: 0,
              background: radialBg,
            }}
          />

          {/* Spotlight Grid Overlay */}
          <motion.div
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.16,
              backgroundImage: `
                linear-gradient(rgba(56, 189, 248, 0.35) 1px, transparent 1px),
                linear-gradient(90deg, rgba(56, 189, 248, 0.35) 1px, transparent 1px)
              `,
              backgroundSize: '32px 32px',
              WebkitMaskImage: radialMask,
              maskImage: radialMask,
            }}
          />
        </div>
      )}

      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          y: totalBgY,
          zIndex: 0,
          overflow: 'hidden',
          willChange: 'transform',
          pointerEvents: 'none',
        }}
      >
        <motion.img
          src={BG_URL}
          alt=""
          initial={{
            scale: 1.08,
            y: '14%',
          }}
          animate={{
            scale: 1.08,
            y: '0%',
          }}
          transition={{
            duration: 2,
            delay: 0.15,
            ease: [0.16, 1, 0.3, 1],
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center bottom',
            display: 'block',
          }}
        />
      </motion.div>

      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '42%',
          background: `linear-gradient(
            to bottom,
            rgba(16, 24, 38, 0) 0%,
            rgba(16, 24, 38, 0.35) 35%,
            rgba(16, 24, 38, 0.82) 72%,
            ${DEEP} 100%
          )`,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      <motion.div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', zIndex: 10, padding: isMobile ? '0 20px' : '0 48px', y: smoothTextY, willChange: 'transform' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, marginBottom: isMobile ? 24 : 32, maxWidth: '100%' }}>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.55, delay: 0.05, ease: [0.65, 0, 0.35, 1] }} style={{ width: isMobile ? 20 : 40, height: 1, background: ACCENT, transformOrigin: 'right', flexShrink: 0 }} />
          <span className="summit-eyebrow-text" style={{ fontFamily: FONT_MONO, fontSize: isMobile ? 9 : 11, letterSpacing: isMobile ? '0.2em' : '0.3em', textTransform: 'uppercase', color: ACCENT, display: 'inline-block', willChange: 'transform', whiteSpace: 'nowrap' }}>
            <SplitText text="AI-POWERED · BUILT FOR THE FUTURE" delay={0.22} perChar={0.018} duration={0.55} />
          </span>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.55, delay: 0.05, ease: [0.65, 0, 0.35, 1] }} style={{ width: isMobile ? 20 : 40, height: 1, background: ACCENT, transformOrigin: 'left', flexShrink: 0 }} />
        </div>

        <motion.h1
          initial={{ clipPath: 'inset(0 100% 0 0)' }}
          animate={{
            clipPath: 'inset(0 0% 0 0)',
          }}
          transition={{
            duration: 1.05,
            delay: 0.55,
            ease: [0.85, 0, 0.15, 1],
          }}
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(62px, 10vw, 120px)',
            color: DARK,
            lineHeight: 0.9,
            letterSpacing: isMobile ? '0.04em' : '0.06em',
            margin: '0 0 8px'
          }}
        >
          BUILT FOR
        </motion.h1>

        <motion.h1
          initial={{
            opacity: 0,
            clipPath: 'inset(0 100% 0 0)',
          }}
          animate={{
            opacity: 1,
            clipPath: 'inset(0 0% 0 0)',
          }}
          transition={{
            duration: 1.4,
            delay: 0.95,
            ease: [0.85, 0, 0.15, 1],
          }}
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: isMobile
              ? 'clamp(48px, 13vw, 72px)'
              : 'clamp(62px, 10vw, 120px)',
            lineHeight: 0.9,
            letterSpacing: isMobile ? '0.04em' : '0.06em',
            margin: isMobile ? '0 0 28px' : '0 0 40px',
            color: 'transparent',
          }}
        >
          <SummitTextFlip words={heroWords} />
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 14, filter: 'blur(8px)' }} animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }} transition={{ duration: 0.95, delay: 1.45, ease: [0.22, 1, 0.36, 1] }} style={{ fontFamily: FONT_BODY, fontSize: isMobile ? 14 : 16, fontWeight: 300, color: 'var(--text-muted)', lineHeight: 1.65, maxWidth: isMobile ? '100%' : 540, margin: isMobile ? '0 0 32px' : '0 0 40px', letterSpacing: '0.02em' }}>
          AI-powered platforms and intelligent agents engineered to transform complex workflows, automate decisions, and accelerate the way modern businesses operate.
        </motion.p>

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 16, alignItems: 'center', width: isMobile ? '100%' : undefined }}>
          <SummitHeroButton variant="primary" delay={1.85} isMobile={isMobile} label="EXPLORE SOLUTIONS" initialX={isMobile ? 0 : -14} />
          <SummitHeroButton variant="secondary" delay={1.97} isMobile={isMobile} label="OUR TECHNOLOGY" initialX={isMobile ? 0 : 14} />
        </div>

        <motion.div initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { delayChildren: 2.25, staggerChildren: 0.12 } } }} style={{ position: 'absolute', bottom: isMobile ? 24 : isTablet ? 32 : 48, right: isMobile ? 20 : isTablet ? 32 : 48, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <motion.span variants={{ hidden: { opacity: 0, y: -6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } } }} style={{ fontFamily: FONT_MONO, fontSize: isMobile ? 8 : 9, letterSpacing: '0.2em', color: STEEL, textTransform: 'uppercase' }}>POWERED BY</motion.span>
          <motion.span variants={{ hidden: { opacity: 0, scale: 0.94 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } }} style={{ fontFamily: FONT_DISPLAY, fontSize: isMobile ? 24 : isTablet ? 30 : 36, color: DARK, letterSpacing: '0.05em', lineHeight: 1, display: 'flex', alignItems: 'baseline', gap: 2 }}>
            AI
          </motion.span>
          <motion.span variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } } }} style={{ fontFamily: FONT_MONO, fontSize: isMobile ? 8 : 9, letterSpacing: '0.2em', color: STEEL, textTransform: 'uppercase' }}>ENTERPRISE READY</motion.span>
        </motion.div>

      </motion.div>
    </section>
  )
}

const INDUSTRIES = [
  {
    id: 'agri',
    icon: '◈',
    label: 'Agriculture',
    image: '/agriculture.jpg',
    color: '#4ade80',
    eyebrow: 'AGRICULTURAL INTELLIGENCE · VOICE · WEATHER',
    points: [
      'Weather intelligence & crop advisory',
      'Voice-enabled farmer assistance',
      'Multilingual support in regional languages',
      'Pest & disease early warning systems',
    ],
  },
  {
    id: 'health',
    icon: '◎',
    label: 'Healthcare',
    image: '/healthcare.jpg',
    color: '#06b6d4',
    eyebrow: 'PATIENT PORTAL · DOCUMENTATION · ASSISTANCE',
    points: [
      'Patient engagement & appointment automation',
      'Medical transcription & documentation',
      'Healthcare voice assistants',
      'Multilingual patient communication',
    ],
  },
  {
    id: 'banking',
    icon: '◇',
    label: 'Banking & Finance',
    image: '/banking.jpg',
    color: '#38bdf8',
    eyebrow: 'FINTECH CUSTOMER SUPPORT · COMPLIANCE · REPORTING',
    points: [
      'Customer support automation',
      'Document intelligence & KYC automation',
      'Compliance workflow management',
      'Intelligent financial reporting',
    ],
  },
  {
    id: 'logistics',
    icon: '◉',
    label: 'Logistics',
    image: '/logistics.jpg',
    color: '#fbbf24',
    eyebrow: 'LOGISTICS SUPPLY CHAIN · DATA TRACKING',
    points: [
      'Shipment tracking & notifications',
      'Route intelligence & optimization',
      'Predictive delay alerts',
      'Operational workflow automation',
    ],
  },
  {
    id: 'edu',
    icon: '◆',
    label: 'Education',
    image: '/education.jpg',
    color: '#a78bfa',
    eyebrow: 'DIGITAL TUTORING · MULTILINGUAL EDUCATION',
    points: [
      'AI tutors & learning assistants',
      'Multilingual education platforms',
      'Voice-based learning systems',
      'Automated assessment & grading',
    ],
  },
  {
    id: 'gov',
    icon: '◈',
    label: 'Government',
    image: '/government.jpg',
    color: '#06b6d4',
    eyebrow: 'PUBLIC ASSISTANCE · DIGITAL GOVERNANCE',
    points: [
      'Citizen service automation',
      'Document processing & digitization',
      'Multilingual public support',
      'Digital governance solutions',
    ],
  },
]

function IndustriesSection() {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.12 }
    )

    if (ref.current) observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const updateDevice = () => {
      const width = window.innerWidth
      setIsMobile(width < 768)
      setIsTablet(width >= 768 && width < 1024)
    }

    updateDevice()
    window.addEventListener('resize', updateDevice)

    return () => window.removeEventListener('resize', updateDevice)
  }, [])

  const activeIndustry = INDUSTRIES[activeIndex]

  return (
    <section
      id="industries"
      ref={ref}
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        background: DEEP,
        color: WHITE,
        padding: isMobile
          ? '100px 16px 80px'
          : isTablet
            ? '110px 20px 90px'
            : 'clamp(115px, 12vw, 160px) 20px',
        zIndex: 250,
      }}
    >
      {/* Ambient background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: 'min(700px, 70vw)',
            height: 'min(700px, 70vw)',
            top: '5%',
            right: '-15%',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(96,165,250,0.09) 0%, transparent 68%)',
            filter: 'blur(90px)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.018,
            backgroundImage: `
              linear-gradient(rgba(125,211,252,1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(125,211,252,1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Heading */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 950,
          margin: '0 auto 64px',
          textAlign: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            color: ACCENT_ICE,
            textTransform: 'uppercase',
          }}
        >
          ◆ &nbsp; 06 / INDUSTRIES WE EMPOWER
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{
            delay: 0.15,
            duration: 0.7,
          }}
          style={{
            margin: '18px 0 0',
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(48px, 7vw, 88px)',
            lineHeight: 0.92,
            letterSpacing: '0.03em',
            color: WHITE,
          }}
        >
          BUILT FOR
          <br />
          <span
            style={{
              display: 'inline-block',
              backgroundImage:
                'linear-gradient(90deg, #38bdf8 0%, #60a5fa 50%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              WebkitTextFillColor: 'transparent',
            }}
          >
            EVERY INDUSTRY
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          style={{
            maxWidth: 620,
            margin: '20px auto 0',
            fontFamily: FONT_BODY,
            fontSize: 'clamp(13px, 1.4vw, 16px)',
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.46)',
          }}
        >
          AI solutions that adapt to the way your industry works.
        </motion.p>
      </div>

      {/* Focus layout */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 1180,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile || isTablet ? '1fr' : '1.1fr 0.9fr',
          gap: isMobile ? 32 : isTablet ? 40 : 48,
          alignItems: 'center',
        }}
      >
        {/* 3D Image Ring visual */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: isMobile ? 340 : 440,
          }}
        >
          <ThreeDImageRing
            images={INDUSTRIES.map((ind) => ind.image)}
            labels={INDUSTRIES.map((ind) => ind.label)}
            colors={INDUSTRIES.map((ind) => ind.color)}
            onActiveChange={(index) => {
              setActiveIndex(index)
            }}
            width={isMobile ? 180 : 220}
            imageDistance={isMobile ? 260 : 340}
            hoverOpacity={0.3}
          />

          {/* Status text "DRAG TO EXPLORE" */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 24,
              fontFamily: FONT_MONO,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: 'rgba(255, 255, 255, 0.4)',
              textTransform: 'uppercase',
            }}
          >
            <motion.span
              animate={{ x: [-2, 2, -2] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
              style={{ display: 'inline-block', fontSize: 12, color: '#38bdf8' }}
            >
              ←
            </motion.span>
            <span>DRAG TO EXPLORE</span>
            <motion.span
              animate={{ x: [2, -2, 2] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
              style={{ display: 'inline-block', fontSize: 12, color: '#38bdf8' }}
            >
              →
            </motion.span>
          </div>
        </div>

        {/* Focused industry details card */}
        <div style={{ position: 'relative', width: '100%' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndustry.id}
              initial={{
                opacity: 0,
                x: isMobile ? 0 : 20,
                y: isMobile ? 10 : 0,
                scale: isMobile ? 0.99 : 0.98,
              }}
              animate={{
                opacity: 1,
                x: 0,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                x: isMobile ? 0 : -12,
                y: isMobile ? -6 : 0,
                scale: 0.99,
              }}
              transition={{
                duration: 0.35,
                ease: [0.16, 1, 0.3, 1],
              }}
              style={{
                position: 'relative',
                minHeight: isMobile ? 0 : isTablet ? 360 : 390,
                padding: isMobile
                  ? '24px 20px'
                  : isTablet
                    ? '28px'
                    : 'clamp(28px, 3.2vw, 38px)',
                overflow: 'hidden',
                background:
                  'linear-gradient(145deg, rgba(255,255,255,0.065), rgba(255,255,255,0.018))',
                border: `1px solid ${activeIndustry.color}55`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 25px 70px rgba(0,0,0,0.24), 0 0 50px ${activeIndustry.color}10`,
                clipPath:
                  'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
              }}
            >
              {/* accent top border line */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '45%',
                  height: 2,
                  background: `linear-gradient(90deg, ${activeIndustry.color}, transparent)`,
                }}
              />

              {/* glow */}
              <div
                style={{
                  position: 'absolute',
                  width: 300,
                  height: 300,
                  top: -100,
                  right: -90,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${activeIndustry.color}18 0%, transparent 70%)`,
                  filter: 'blur(20px)',
                  pointerEvents: 'none',
                }}
              />

              <div
                style={{
                  position: 'relative',
                  zIndex: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 9,
                    letterSpacing: '0.2em',
                    color: activeIndustry.color,
                    textTransform: 'uppercase',
                  }}
                >
                  {activeIndustry.eyebrow}
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    marginTop: 20,
                  }}
                >
                  <motion.div
                    animate={{
                      scale: [0.95, 1.05, 1],
                    }}
                    transition={{ duration: 0.4 }}
                    style={{
                      width: 58,
                      height: 58,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `${activeIndustry.color}12`,
                      border: `1px solid ${activeIndustry.color}55`,
                      color: activeIndustry.color,
                      fontSize: 24,
                      boxShadow: `0 0 24px ${activeIndustry.color}12`,
                    }}
                  >
                    {activeIndustry.icon}
                  </motion.div>

                  <h3
                    style={{
                      margin: 0,
                      fontFamily: FONT_DISPLAY,
                      fontSize: 'clamp(32px, 4vw, 54px)',
                      lineHeight: 0.95,
                      letterSpacing: '0.04em',
                      color: WHITE,
                      textTransform: 'uppercase',
                    }}
                  >
                    {activeIndustry.label}
                  </h3>
                </div>

                <div
                  style={{
                    marginTop: 36,
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: 14,
                  }}
                >
                  {activeIndustry.points.map((point, index) => (
                    <motion.div
                      key={point}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: index * 0.06,
                      }}
                      whileHover={{
                        scale: 1.02,
                        borderColor: `${activeIndustry.color}44`,
                        background: 'rgba(255,255,255,0.04)',
                      }}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '16px 14px',
                        background: 'rgba(255,255,255,0.025)',
                        border: '1px solid rgba(200,230,255,0.07)',
                        transition: 'border-color 0.2s, background 0.2s',
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          marginTop: 6,
                          flexShrink: 0,
                          borderRadius: '50%',
                          background: activeIndustry.color,
                          boxShadow: `0 0 8px ${activeIndustry.color}`,
                        }}
                      />

                      <span
                        style={{
                          fontFamily: FONT_MONO,
                          fontSize: 9,
                          lineHeight: 1.6,
                          color: 'rgba(255,255,255,0.58)',
                        }}
                      >
                        {point}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 'auto',
                    paddingTop: 40,
                    fontFamily: FONT_MONO,
                    fontSize: 9,
                    letterSpacing: '0.16em',
                    color: activeIndustry.color,
                    textTransform: 'uppercase',
                  }}
                >
                  GLIEDAI · INDUSTRY INTELLIGENCE
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{
          duration: 1.1,
          ease: [0.16, 1, 0.3, 1],
        }}
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          width: 'min(360px, 60vw)',
          height: 1,
          transform: 'translateX(-50%)',
          background:
            'linear-gradient(90deg, transparent, rgba(56,189,248,0.7), transparent)',
        }}
      />
    </section>
  )
}

const IMPACTS = [
  {
    metric: '10×',
    label: 'Faster Response Times',
    sub: 'VS. MANUAL WORKFLOWS',
    desc:
      'Intelligent automation compresses multi-hour processes into seconds of real-time AI action.',
  },
  {
    metric: '70%',
    label: 'Reduced Costs',
    sub: 'OPERATIONAL SAVINGS',
    desc:
      'Replace repetitive overhead with self-managing AI infrastructure that scales without headcount.',
  },
  {
    metric: '24/7',
    label: 'Always Active',
    sub: 'ZERO DOWNTIME',
    desc:
      'Fully autonomous systems that never sleep, handle peak loads, and operate across every timezone.',
  },
  {
    metric: '100+',
    label: 'Languages Supported',
    sub: 'MULTILINGUAL REACH',
    desc:
      'Communicate, process, and respond in over 100 languages across every market you serve.',
  },
]

const IMPACT_OUTCOMES = [
  { icon: '◎', label: 'Faster Response Times' },
  { icon: '◈', label: 'Multilingual Reach' },
  { icon: '◉', label: 'Intelligent Automation' },
  { icon: '◇', label: 'Seamless Integration' },
  { icon: '◆', label: 'Enterprise Scale' },
]

function parseMetric(metric: string) {
  const match = metric.match(/^(\d+)(.*)$/)
  if (match) {
    return {
      value: parseInt(match[1], 10),
      suffix: match[2],
    }
  }
  return { value: 0, suffix: metric }
}

function ImpactItem({
  item,
  index,
  isVisible,
}: {
  item: (typeof IMPACTS)[number]
  index: number
  isVisible: boolean
}) {
  const [lit, setLit] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    if (!isVisible || lit) return

    const timer = window.setTimeout(() => {
      setLit(true)
    }, 300 + index * 250)

    return () => window.clearTimeout(timer)
  }, [isVisible, lit, index])

  const { value, suffix } = parseMetric(item.metric)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!lit) return
    let rafId = 0
    const duration = 1.4
    const startMs = performance.now()
    const tick = (now: number) => {
      const elapsed = now - startMs
      const t = Math.min(elapsed / (duration * 1000), 1)
      setCount(Math.round(value * (1 - Math.pow(1 - t, 4))))
      if (t < 1) {
        rafId = requestAnimationFrame(tick)
      }
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [lit, value])

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      tabIndex={0}
      transition={{
        duration: 0.8,
        delay: index * 0.15,
        ease: [0.16, 1, 0.3, 1],
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        cursor: 'pointer',
        outline: 'none',
      }}
    >
      <div
        style={{
          position: 'relative',
          marginBottom: 16,
          height: 'clamp(70px, 10vw, 110px)',
          display: 'flex',
          alignItems: 'baseline',
        }}
      >
        <motion.span
          animate={{
            textShadow: isHovered
              ? '0 0 45px rgba(56,189,248,0.65), 0 0 15px rgba(56,189,248,0.3)'
              : lit
                ? '0 0 25px rgba(56,189,248,0.22)'
                : 'none',
            WebkitTextStroke: isHovered
              ? `1.5px #38bdf8`
              : lit
                ? `1.5px ${ACCENT_ICE}`
                : '1px rgba(125,211,252,0.18)',
          }}
          transition={{
            duration: 0.3,
          }}
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(58px, 8vw, 110px)',
            lineHeight: 1,
            fontWeight: 400,
            color: lit ? WHITE : 'transparent',
            letterSpacing: '-0.02em',
            transition: 'color 0.4s ease',
          }}
        >
          {lit ? count : 0}
          {suffix}
        </motion.span>
      </div>

      <h3
        style={{
          margin: '0 0 6px',
          fontFamily: FONT_DISPLAY,
          fontSize: 'clamp(15px, 1.8vw, 20px)',
          letterSpacing: '0.04em',
          color: WHITE,
          textTransform: 'uppercase',
        }}
      >
        {item.label}
      </h3>

      <div
        style={{
          marginBottom: 12,
          fontFamily: FONT_MONO,
          fontSize: 9,
          letterSpacing: '0.13em',
          color: ACCENT_ICE,
          textTransform: 'uppercase',
        }}
      >
        {item.sub}
      </div>

      <motion.p
        animate={{
          x: isHovered ? 6 : 0,
        }}
        transition={{
          duration: 0.3,
          ease: 'easeOut',
        }}
        style={{
          margin: 0,
          fontFamily: FONT_BODY,
          fontSize: 13,
          lineHeight: 1.6,
          color: 'rgba(255,255,255,0.46)',
        }}
      >
        {item.desc}
      </motion.p>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={
          isVisible
            ? {
                scaleX: isHovered ? 1.08 : 1,
              }
            : { scaleX: 0 }
        }
        transition={
          isHovered
            ? { duration: 0.3, ease: 'easeOut' }
            : { delay: 0.5 + index * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }
        }
        style={{
          marginTop: 24,
          width: '100%',
          height: isHovered ? 2 : 1,
          transformOrigin: 'left',
          background: isHovered
            ? 'linear-gradient(to right, #38bdf8, transparent)'
            : 'linear-gradient(to right, rgba(56,189,248,0.35), transparent)',
          transition: 'background 0.3s ease, height 0.3s ease',
        }}
      />
    </motion.div>
  )
}

function ImpactSection() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.12 }
    )

    if (ref.current) observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  return (
    <section
      id="impact"
      ref={ref}
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        background: DEEP,
        color: WHITE,
        padding: 'clamp(115px, 12vw, 160px) 20px',
        zIndex: 250,
      }}
    >
      {/* Vortex background environment */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.28,
          pointerEvents: 'none',
        }}
      >
        <Vortex
          backgroundColor={DEEP}
          particleCount={80}
          baseSpeed={0.25}
          rangeSpeed={0.9}
          baseRadius={0.6}
          rangeRadius={1.3}
        />
      </div>

      {/* Ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(700px, 80vw)',
          height: 'min(700px, 80vw)',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(56,189,248,0.045) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.018,
          pointerEvents: 'none',
          backgroundImage: `
            linear-gradient(rgba(125,211,252,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(125,211,252,1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: 1280,
          margin: '0 auto',
        }}
      >
        {/* Heading */}
        <div
          style={{
            maxWidth: 950,
            margin: '0 auto 64px',
            textAlign: 'center',
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.22em',
              color: ACCENT_ICE,
              textTransform: 'uppercase',
            }}
          >
            ◆ &nbsp; 07 / MEASURABLE IMPACT
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{
              delay: 0.15,
              duration: 0.7,
            }}
            style={{
              margin: '18px 0 0',
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(48px, 7vw, 88px)',
              lineHeight: 0.92,
              letterSpacing: '0.03em',
              color: WHITE,
            }}
          >
            OUTCOMES THAT
            <br />
            <span
              style={{
                display: 'inline-block',
                backgroundImage:
                  'linear-gradient(90deg, #38bdf8 0%, #60a5fa 50%, #8b5cf6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
              }}
            >
              DRIVE GROWTH
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 }}
            style={{
              maxWidth: 620,
              margin: '20px auto 0',
              fontFamily: FONT_BODY,
              fontSize: 'clamp(13px, 1.4vw, 16px)',
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.46)',
            }}
          >
            Real business results delivered through intelligent automation at enterprise scale.
          </motion.p>
        </div>

        {/* Outcome pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 10,
            marginBottom: 72,
          }}
        >
          {IMPACT_OUTCOMES.map(outcome => (
            <div
              key={outcome.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '9px 14px',
                background: 'rgba(255,255,255,0.035)',
                border: '1px solid rgba(200,230,255,0.09)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: ACCENT_ICE,
                }}
              >
                {outcome.icon}
              </span>

              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 8,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.48)',
                }}
              >
                {outcome.label}
              </span>
            </div>
          ))}
        </motion.div>

        {/* Metrics */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 'clamp(28px, 4vw, 64px)',
          }}
        >
          {IMPACTS.map((item, index) => (
            <ImpactItem
              key={item.metric}
              item={item}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>

        {/* Quote */}
        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{
            delay: 0.9,
          }}
          style={{
            marginTop: 90,
            textAlign: 'center',
            padding: '32px clamp(20px, 5vw, 48px)',
            borderTop: '1px solid rgba(200,230,255,0.08)',
            borderBottom: '1px solid rgba(200,230,255,0.08)',
          }}
        >
          <p
            style={{
              maxWidth: 720,
              margin: '0 auto',
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(20px, 2.7vw, 30px)',
              lineHeight: 1.35,
              letterSpacing: '0.03em',
              color: 'rgba(255,255,255,0.74)',
            }}
          >
            “The question is not whether your business will use AI —
            <span style={{ color: ACCENT_ICE }}>
              {' '}it&apos;s whether you&apos;ll lead or follow.
            </span>
          </p>

          <div
            style={{
              marginTop: 18,
              fontFamily: FONT_MONO,
              fontSize: 9,
              letterSpacing: '0.18em',
              color: STEEL,
              textTransform: 'uppercase',
            }}
          >
            GLIEDAI &nbsp; ◆ &nbsp; 2026
          </div>
        </motion.div>
      </div>
    </section>
  )
}

function ConvergingNetwork() {
  const groupRef = useRef<THREE.Group>(null)
  const nodeRefs = useRef<
    Array<THREE.Mesh | null>
  >([])

  const count = 32

  const nodes = useRef(
    Array.from({ length: count }, () => ({
      start: new THREE.Vector3(
        (Math.random() - 0.5) * 9,
        (Math.random() - 0.5) * 6,
        (Math.random() - 0.5) * 4
      ),
      current: new THREE.Vector3(),
    }))
  )

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    const raw = Math.min(1, t / 4.5)

    const ease =
      raw < 0.5
        ? 2 * raw * raw
        : -1 + (4 - 2 * raw) * raw

    nodes.current.forEach((node, index) => {
      const wobble =
        Math.sin(t * 0.45 + index * 0.8) *
        (1 - ease) *
        0.35

      node.current.lerpVectors(
        node.start,
        new THREE.Vector3(0, 0, 0),
        ease * 0.88
      )

      const mesh = nodeRefs.current[index]

      if (mesh) {
        mesh.position.copy(node.current)
        mesh.position.y += wobble
      }
    })

    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.09
    }
  })

  return (
    <group ref={groupRef}>
      {nodes.current.map((node, index) => (
        <mesh
          key={index}
          ref={(element: THREE.Mesh | null) => {
            nodeRefs.current[index] = element
          }}
          position={node.start}
        >
          <sphereGeometry args={[0.038, 6, 6]} />

          <meshBasicMaterial
            color="#38bdf8"
            transparent
            opacity={0.5}
          />

          <pointLight
            color="#38bdf8"
            intensity={0.18}
            distance={1.2}
          />
        </mesh>
      ))}

      <mesh>
        <sphereGeometry args={[0.16, 16, 16]} />

        <meshBasicMaterial
          color="#60a5fa"
          transparent
          opacity={0.9}
        />

        <pointLight
          color="#60a5fa"
          intensity={4}
          distance={8}
        />
      </mesh>
    </group>
  )
}

interface VortexProps {
  children?: React.ReactNode
  containerClassName?: string
  className?: string
  backgroundColor?: string
  particleCount?: number
  baseSpeed?: number
  rangeSpeed?: number
  baseRadius?: number
  rangeRadius?: number
}

export function Vortex({
  children,
  containerClassName,
  className,
  backgroundColor = '#101826',
  particleCount = 100,
  baseSpeed = 0.35,
  rangeSpeed = 1.2,
  baseRadius = 0.8,
  rangeRadius = 1.6,
}: VortexProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const mouseRef = useRef({ x: 0, y: 0, active: false })

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationFrameId: number
    let width = (canvas.width = container.clientWidth)
    let height = (canvas.height = container.clientHeight)

    const handleResize = () => {
      if (!container || !canvas) return
      width = canvas.width = container.clientWidth
      height = canvas.height = container.clientHeight
    }
    window.addEventListener('resize', handleResize)

    class Particle {
      x: number
      y: number
      vx: number
      vy: number
      radius: number
      alpha: number
      color: string

      constructor() {
        this.x = Math.random() * width
        this.y = Math.random() * height
        const angle = Math.random() * Math.PI * 2
        const speed = baseSpeed + Math.random() * rangeSpeed
        this.vx = Math.cos(angle) * speed
        this.vy = Math.sin(angle) * speed
        this.radius = baseRadius + Math.random() * rangeRadius
        this.alpha = 0.2 + Math.random() * 0.4
        const colors = ['#38bdf8', '#60a5fa', '#8b5cf6', '#a78bfa']
        this.color = colors[Math.floor(Math.random() * colors.length)]
      }

      update() {
        const dx = this.x - width / 2
        const dy = this.y - height / 2
        const dist = Math.sqrt(dx * dx + dy * dy)
        const force = 0.02
        this.vx += (-dy / (dist + 10)) * force
        this.vy += (dx / (dist + 10)) * force

        if (mouseRef.current.active) {
          const mdx = mouseRef.current.x - this.x
          const mdy = mouseRef.current.y - this.y
          const mdist = Math.sqrt(mdx * mdx + mdy * mdy)
          if (mdist < 300) {
            const pull = (1 - mdist / 300) * 0.08
            this.vx += mdx * pull * 0.05
            this.vy += mdy * pull * 0.05
          }
        }

        this.x += this.vx
        this.y += this.vy
        this.vx *= 0.98
        this.vy *= 0.98

        if (this.x < 0 || this.x > width || this.y < 0 || this.y > height) {
          this.x = Math.random() * width
          this.y = Math.random() * height
          const angle = Math.random() * Math.PI * 2
          const speed = baseSpeed + Math.random() * rangeSpeed
          this.vx = Math.cos(angle) * speed
          this.vy = Math.sin(angle) * speed
        }
      }

      draw() {
        if (!ctx) return
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2)
        ctx.fillStyle = this.color
        ctx.globalAlpha = this.alpha
        ctx.fill()
      }
    }

    const particles: Particle[] = []
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle())
    }

    const drawLines = () => {
      const maxDist = 90
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.12
            ctx.beginPath()
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.strokeStyle = '#60a5fa'
            ctx.globalAlpha = alpha
            ctx.lineWidth = 0.5
            ctx.stroke()
          }
        }
      }
    }

    const render = () => {
      ctx.clearRect(0, 0, width, height)
      
      const isDark = document.documentElement.classList.contains('dark')
      const resolvedBg = isDark ? '#070b10' : '#f5f8fb'
      ctx.fillStyle = resolvedBg
      ctx.globalAlpha = 1
      ctx.fillRect(0, 0, width, height)

      const gradient = ctx.createRadialGradient(
        width / 2,
        height / 2,
        10,
        width / 2,
        height / 2,
        Math.max(width, height) * 0.6
      )
      const stop0 = isDark ? 'rgba(14, 22, 33, 0.5)' : 'rgba(237, 243, 247, 0.5)'
      const stop1 = isDark ? '#070b10' : '#f5f8fb'
      gradient.addColorStop(0, stop0)
      gradient.addColorStop(1, stop1)
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, width, height)

      particles.forEach((p) => {
        p.update()
        p.draw()
      })

      drawLines()

      animationFrameId = requestAnimationFrame(render)
    }

    render()

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouseRef.current.x = e.clientX - rect.left
      mouseRef.current.y = e.clientY - rect.top
      mouseRef.current.active = true
    }

    const handleMouseLeave = () => {
      mouseRef.current.active = false
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      window.removeEventListener('resize', handleResize)
      cancelAnimationFrame(animationFrameId)
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove)
        container.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [backgroundColor, particleCount, baseSpeed, rangeSpeed, baseRadius, rangeRadius])

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'block',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', zIndex: 10, width: '100%', height: '100%' }}>
        {children}
      </div>
    </div>
  )
}

export function FloatingDock() {
  const [show, setShow] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setShow(window.scrollY > 400)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const items = [
    {
      label: 'Home',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      onClick: () => {
        if (window.location.pathname !== '/') {
          window.location.href = '/'
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      },
    },
    {
      label: 'Solutions',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="7" height="9" x="3" y="3" rx="1" />
          <rect width="7" height="5" x="14" y="3" rx="1" />
          <rect width="7" height="9" x="14" y="12" rx="1" />
          <rect width="7" height="5" x="3" y="16" rx="1" />
        </svg>
      ),
      onClick: () => {
        if (window.location.pathname !== '/') {
          window.location.href = '/#solutions'
        } else {
          document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' })
        }
      },
    },
    {
      label: 'Industries',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="14" x="2" y="7" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      ),
      onClick: () => {
        if (window.location.pathname !== '/') {
          window.location.href = '/#industries'
        } else {
          document.getElementById('industries')?.scrollIntoView({ behavior: 'smooth' })
        }
      },
    },
    {
      label: 'Contact',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="16" x="2" y="4" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      ),
      onClick: () => {
        if (window.location.pathname !== '/') {
          window.location.href = '/#contact'
        } else {
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
        }
      },
    },
  ]

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 30, x: '-50%' }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            background: 'rgba(16, 24, 38, 0.72)',
            border: '1px solid rgba(200, 230, 255, 0.15)',
            borderRadius: '9999px',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4), 0 0 15px rgba(56,189,248,0.06)',
          }}
        >
          {items.map((item, idx) => {
            const isHovered = hoveredIndex === idx
            return (
              <div
                key={item.label}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ position: 'relative' }}
              >
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: -28, scale: 1 }}
                      exit={{ opacity: 0, y: 5, scale: 0.95 }}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                        background: 'rgba(8,11,12,0.95)',
                        border: '1px solid rgba(200, 230, 255, 0.12)',
                        color: WHITE,
                        padding: '4px 8px',
                        fontSize: 9,
                        fontFamily: FONT_MONO,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        borderRadius: 4,
                        pointerEvents: 'none',
                        zIndex: 10000,
                      }}
                    >
                      {item.label}
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.button
                  type="button"
                  onClick={item.onClick}
                  animate={{
                    scale: isHovered ? 1.25 : 1,
                    backgroundColor: isHovered ? 'rgba(56,189,248,0.15)' : 'rgba(255,255,255,0.02)',
                    borderColor: isHovered ? 'rgba(56,189,248,0.45)' : 'rgba(200,230,255,0.08)',
                  }}
                  transition={{ type: 'spring', stiffness: 350, damping: 25 }}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '1px solid rgba(200,230,255,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isHovered ? '#38bdf8' : 'rgba(200,230,255,0.7)',
                    cursor: 'pointer',
                    outline: 'none',
                    padding: 0,
                  }}
                >
                  {item.icon}
                </motion.button>
              </div>
            )
          })}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface ConfettiProps {
  active: boolean
}

function Confetti({ active }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!active) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationFrameId: number

    canvas.width = canvas.parentElement?.clientWidth || window.innerWidth
    canvas.height = canvas.parentElement?.clientHeight || window.innerHeight

    const colors = ['#38bdf8', '#60a5fa', '#8b5cf6', '#06b6d4', '#c8e6ff']
    const particles: Array<{
      x: number
      y: number
      size: number
      color: string
      vx: number
      vy: number
      rotation: number
      rotationSpeed: number
    }> = []

    const particleCount = 100
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: canvas.width / 2,
        y: canvas.height / 2,
        size: Math.random() * 6 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 16,
        vy: -Math.random() * 14 - 6,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 8
      })
    }

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      let alive = false
      particles.forEach(p => {
        p.vy += 0.24
        p.vx *= 0.985
        p.vy *= 0.985

        p.x += p.vx
        p.y += p.vy
        p.rotation += p.rotationSpeed

        if (p.y < canvas.height && p.x > 0 && p.x < canvas.width) {
          alive = true
        }

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate((p.rotation * Math.PI) / 180)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size)
        ctx.restore()
      })

      if (alive) {
        animationFrameId = requestAnimationFrame(render)
      }
    }

    render()

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [active])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 50,
      }}
    />
  )
}

type ContactFormState = {
  name: string
  businessName: string
  email: string
  phone: string
  businessType: string
  businessSize: string
  challenge: string
}

function ContactSection() {
  const [isVisible, setIsVisible] = useState(false)
  const [sent, setSent] = useState(false)
  const [form, setForm] = useState<ContactFormState>({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    businessType: '',
    businessSize: '',
    challenge: '',
  })

  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  const updateField = <K extends keyof ContactFormState>(
    field: K,
    value: ContactFormState[K]
  ) => {
    setForm(previous => ({
      ...previous,
      [field]: value,
    }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (form.name && form.email) {
      setSent(true)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    background: 'rgba(255,255,255,0.035)',
    border: '1px solid rgba(200,230,255,0.10)',
    color: WHITE,
    outline: 'none',
    fontFamily: FONT_BODY,
    fontSize: 14,
  }

  return (
    <section
      id="contact"
      ref={ref}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100svh',
        overflow: 'hidden',
        background: DEEP,
        color: WHITE,
        padding: 'clamp(80px, 9vw, 130px) 20px',
      }}
    >
      {/* Vortex background environment */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          pointerEvents: 'none',
        }}
      >
        <Vortex
          backgroundColor={DEEP}
          particleCount={90}
          baseSpeed={0.3}
          rangeSpeed={1.0}
          baseRadius={0.8}
          rangeRadius={1.5}
        />
      </div>

      {/* Dark overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse at center, rgba(16,24,38,0.1) 0%, rgba(16,24,38,0.92) 80%)',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: 1280,
          margin: '0 auto',
        }}
      >
        {/* Top banner */}
        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={
            isVisible
              ? {
                opacity: 1,
                y: 0,
              }
              : {}
          }
          transition={{
            delay: 0.2,
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            padding:
              'clamp(24px, 4vw, 34px)',
            marginBottom: 80,
            border:
              '1px solid rgba(200,230,255,0.10)',
            background:
              'rgba(255,255,255,0.035)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter:
              'blur(18px)',
            clipPath:
              'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontFamily: FONT_DISPLAY,
                fontSize:
                  'clamp(24px, 3vw, 34px)',
                letterSpacing: '0.03em',
                color: WHITE,
              }}
            >
              READY TO DEPLOY COGNITIVE INFRASTRUCTURE?
            </h3>

            <p
              style={{
                maxWidth: 780,
                margin: '10px 0 0',
                fontFamily: FONT_BODY,
                fontSize: 14,
                lineHeight: 1.7,
                color:
                  'rgba(255,255,255,0.46)',
              }}
            >
              Transform your operations with
              autonomous, multi-modal AI agents
              designed for enterprise-grade scalability
              and seamless integration.
            </p>
          </div>

          <button
            type="button"
            style={{
              alignSelf: 'flex-start',
              padding: '12px 22px',
              border: 0,
              background: DARK,
              color: WHITE,
              fontFamily: FONT_MONO,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              clipPath:
                'polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%)',
            }}
          >
            Discover GLIEDAI Agents
          </button>
        </motion.div>

        {/* Main columns */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'minmax(0,1fr) minmax(0,1fr)',
            gap:
              'clamp(40px, 7vw, 100px)',
            alignItems: 'start',
          }}
        >
          {/* Left */}
          <motion.div
            initial={{
              opacity: 0,
              x: -30,
            }}
            animate={
              isVisible
                ? {
                  opacity: 1,
                  x: 0,
                }
                : {}
            }
            transition={{
              delay: 0.3,
              duration: 0.8,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 28,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 1,
                  background: ACCENT_ICE,
                }}
              />

              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  letterSpacing: '0.24em',
                  textTransform: 'uppercase',
                  color: ACCENT_ICE,
                }}
              >
                ◆ &nbsp; 08 / CONTACT / AI TRANSFORMATION
              </span>
            </div>

            <h2
              style={{
                margin: '0 0 24px',
                fontFamily: FONT_DISPLAY,
                fontSize:
                  'clamp(52px, 7vw, 88px)',
                lineHeight: 0.92,
                letterSpacing: '0.03em',
                color: WHITE,
              }}
            >
              ARCHITECT YOUR
              <br />
              <span
                style={{
                  color: 'transparent',
                  WebkitTextStroke:
                    '1.5px rgba(125,211,252,0.52)',
                }}
              >
                AI TRANSFORMATION.
              </span>
            </h2>

            <p
              style={{
                maxWidth: 590,
                margin: '0 0 36px',
                fontFamily: FONT_BODY,
                fontSize: 16,
                lineHeight: 1.75,
                color:
                  'rgba(255,255,255,0.50)',
              }}
            >
              Schedule a complimentary 30-minute
              infrastructure review. We&apos;ll identify
              operational bottlenecks and blueprint exactly
              how our cognitive agents can drive immediate
              ROI for your business.
            </p>

            <h4
              style={{
                margin: '0 0 18px',
                fontFamily: FONT_DISPLAY,
                fontSize: 24,
                letterSpacing: '0.04em',
                color: WHITE,
              }}
            >
              WHAT TO EXPECT
            </h4>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {[
                {
                  title: 'Infrastructure Audit',
                  desc:
                    'We analyze your existing operational bottlenecks and tech stack.',
                },
                {
                  title: 'Opportunity Mapping',
                  desc:
                    'Pinpoint high-impact areas for immediate AI integration.',
                },
                {
                  title: 'Custom Architecture',
                  desc:
                    'Blueprint a scalable, secure cognitive ecosystem for your enterprise.',
                },
                {
                  title: 'Deployment Timeline',
                  desc:
                    'Receive a transparent integration roadmap and deployment estimate.',
                },
              ].map(item => (
                <div
                  key={item.title}
                  style={{
                    display: 'flex',
                    gap: 14,
                    padding: 14,
                    border:
                      '1px solid rgba(200,230,255,0.07)',
                    background:
                      'rgba(255,255,255,0.025)',
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background:
                        'rgba(56,189,248,0.08)',
                      color: ACCENT_ICE,
                    }}
                  >
                    ✓
                  </div>

                  <div>
                    <h5
                      style={{
                        margin: 0,
                        fontFamily: FONT_BODY,
                        fontSize: 13,
                        color: WHITE,
                      }}
                    >
                      {item.title}
                    </h5>

                    <p
                      style={{
                        margin: '4px 0 0',
                        fontFamily: FONT_BODY,
                        fontSize: 12,
                        lineHeight: 1.55,
                        color:
                          'rgba(255,255,255,0.42)',
                      }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 28,
                fontFamily: FONT_MONO,
                fontSize: 10,
                color: STEEL,
                letterSpacing: '0.08em',
              }}
            >
              WE RESPOND WITHIN 24 HOURS
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={
              isVisible
                ? {
                  opacity: 1,
                  y: 0,
                }
                : {}
            }
            transition={{
              delay: 0.5,
              duration: 0.8,
            }}
          >
            <div
              style={{
                position: 'relative',
                padding:
                  'clamp(24px, 4vw, 40px)',
                background:
                  'rgba(8,11,12,0.84)',
                border:
                  '1px solid rgba(200,230,255,0.11)',
                backdropFilter:
                  'blur(24px)',
                WebkitBackdropFilter:
                  'blur(24px)',
                boxShadow:
                  '0 25px 70px rgba(0,0,0,0.28)',
                clipPath:
                  'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
              }}
            >
              <Confetti active={sent} />
              {sent ? (
                <div
                  style={{
                    minHeight: 520,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      fontSize: 54,
                      color: ACCENT_ICE,
                    }}
                  >
                    ✓
                  </div>

                  <h3
                    style={{
                      margin: '18px 0 10px',
                      fontFamily: FONT_DISPLAY,
                      fontSize: 34,
                      letterSpacing: '0.04em',
                      color: WHITE,
                    }}
                  >
                    REQUEST RECEIVED
                  </h3>

                  <p
                    style={{
                      maxWidth: 420,
                      margin: 0,
                      fontFamily: FONT_BODY,
                      fontSize: 14,
                      lineHeight: 1.7,
                      color:
                        'rgba(255,255,255,0.48)',
                    }}
                  >
                    Thank you, {form.name}.
                    Our AI strategists will contact
                    you at {form.email} shortly.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 20,
                  }}
                >
                  {[
                    {
                      label: 'NAME *',
                      type: 'text',
                      key: 'name',
                      placeholder:
                        'Your full name',
                    },
                    {
                      label: 'BUSINESS NAME *',
                      type: 'text',
                      key: 'businessName',
                      placeholder:
                        'Your company name',
                    },
                    {
                      label: 'EMAIL *',
                      type: 'email',
                      key: 'email',
                      placeholder:
                        'you@company.com',
                    },
                    {
                      label: 'PHONE *',
                      type: 'tel',
                      key: 'phone',
                      placeholder:
                        '+1 234 567 8900',
                    },
                  ].map(field => (
                    <label
                      key={field.key}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 7,
                      }}
                    >
                      <span
                        style={{
                          fontFamily:
                            FONT_MONO,
                          fontSize: 8,
                          letterSpacing:
                            '0.13em',
                          color: STEEL,
                        }}
                      >
                        {field.label}
                      </span>

                      <input
                        required
                        type={field.type}
                        placeholder={
                          field.placeholder
                        }
                        value={
                          form[
                          field.key as keyof ContactFormState
                          ]
                        }
                        onChange={event =>
                          updateField(
                            field.key as keyof ContactFormState,
                            event.target.value
                          )
                        }
                        style={
                          inputStyle
                        }
                      />
                    </label>
                  ))}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        '1fr 1fr',
                      gap: 14,
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 7,
                      }}
                    >
                      <span
                        style={{
                          fontFamily:
                            FONT_MONO,
                          fontSize: 8,
                          letterSpacing:
                            '0.13em',
                          color: STEEL,
                        }}
                      >
                        BUSINESS TYPE *
                      </span>

                      <select
                        required
                        value={
                          form.businessType
                        }
                        onChange={event =>
                          updateField(
                            'businessType',
                            event.target.value
                          )
                        }
                        style={inputStyle}
                      >
                        <option value="">
                          Select type
                        </option>
                        <option value="Healthcare">
                          Healthcare
                        </option>
                        <option value="Logistics">
                          Logistics
                        </option>
                        <option value="Finance">
                          Finance
                        </option>
                        <option value="E-commerce">
                          E-commerce
                        </option>
                        <option value="Other">
                          Other
                        </option>
                      </select>
                    </label>

                    <label
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 7,
                      }}
                    >
                      <span
                        style={{
                          fontFamily:
                            FONT_MONO,
                          fontSize: 8,
                          letterSpacing:
                            '0.13em',
                          color: STEEL,
                        }}
                      >
                        BUSINESS SIZE *
                      </span>

                      <select
                        required
                        value={
                          form.businessSize
                        }
                        onChange={event =>
                          updateField(
                            'businessSize',
                            event.target.value
                          )
                        }
                        style={inputStyle}
                      >
                        <option value="">
                          Select size
                        </option>
                        <option value="1-10">
                          1-10 employees
                        </option>
                        <option value="11-50">
                          11-50 employees
                        </option>
                        <option value="51-200">
                          51-200 employees
                        </option>
                        <option value="201+">
                          201+ employees
                        </option>
                      </select>
                    </label>
                  </div>

                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        fontFamily:
                          FONT_MONO,
                        fontSize: 8,
                        letterSpacing:
                          '0.13em',
                        color: STEEL,
                      }}
                    >
                      BIGGEST CHALLENGE *
                    </span>

                    <textarea
                      required
                      rows={4}
                      placeholder="What is the biggest operational or growth challenge your business faces right now?"
                      value={form.challenge}
                      onChange={event =>
                        updateField(
                          'challenge',
                          event.target.value
                        )
                      }
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                      }}
                    />
                  </label>

                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      marginTop: 6,
                      padding: '16px 20px',
                      border: 0,
                      background:
                        'linear-gradient(90deg, #2563eb, #60a5fa, #8b5cf6)',
                      color: WHITE,
                      fontFamily:
                        FONT_MONO,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing:
                        '0.14em',
                      textTransform:
                        'uppercase',
                      cursor: 'pointer',
                    }}
                  >
                    BOOK MY FREE AUDIT
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const SOLUTIONS = [
  {
    id: 'voice',
    icon: '◎',
    label: 'Voice AI',
    tag: 'ASR · TTS · VOICE ASSISTANTS',
    desc:
      'Real-time speech recognition, intelligent voice assistants, and multilingual communication systems that understand and respond naturally.',
    accent: '#38bdf8',
    points: [
      'Automatic Speech Recognition (ASR)',
      'Neural Text-to-Speech (TTS)',
      'Multilingual voice interfaces',
      'Noise-robust recognition',
    ],
  },
  {
    id: 'trans',
    icon: '◈',
    label: 'Translation Intelligence',
    tag: 'NLP · LOCALIZATION · REAL-TIME',
    desc:
      'Break language barriers with accurate multilingual translation and localization services powered by neural machine translation.',
    accent: '#60a5fa',
    points: [
      '100+ language pairs',
      'Real-time translation APIs',
      'Domain-specific models',
      'Cultural localization',
    ],
  },
  {
    id: 'callbots',
    icon: '◉',
    label: 'AI Call Bots',
    tag: 'CUSTOMER SUPPORT · OUTBOUND · 24/7',
    desc:
      'Automate customer support, lead qualification, appointment scheduling, and outbound campaigns with intelligent voice bots.',
    accent: '#7dd3fc',
    points: [
      '24/7 customer support automation',
      'Intelligent lead qualification',
      'Appointment scheduling',
      'Outbound campaign management',
    ],
  },
  {
    id: 'office',
    icon: '◇',
    label: 'Office Automation',
    tag: 'WORKFLOWS · DOCUMENTS · AI ASSISTANTS',
    desc:
      'Reduce manual work through workflow automation, intelligent document processing, and AI-powered office assistants.',
    accent: '#818cf8',
    points: [
      'Workflow automation',
      'Document processing & OCR',
      'Email & calendar intelligence',
      'Report generation',
    ],
  },
  {
    id: 'weather',
    icon: '◆',
    label: 'Weather Intelligence',
    tag: 'FORECASTING · ALERTS · ANALYTICS',
    desc:
      'Deliver real-time weather insights, automated alerts, hyper-local forecasts, and environmental monitoring for critical operations.',
    accent: '#22d3ee',
    points: [
      'Hyper-local forecasting',
      'Automated weather alerts',
      'Agricultural weather advisory',
      'Risk prediction analytics',
    ],
  },
  {
    id: 'agents',
    icon: '◈',
    label: 'Enterprise AI Agents',
    tag: 'AUTONOMOUS · SCALABLE · CUSTOM',
    desc:
      'Custom AI agents designed to execute complex business workflows, analyze information at scale, and drive measurable outcomes.',
    accent: '#a78bfa',
    points: [
      'Custom workflow execution',
      'Multi-system integration',
      'Autonomous decision-making',
      'Scalable agent deployment',
    ],
  },
]

function SummitFloatingOrb({
  index,
  accent,
}: {
  index: number
  accent?: string
}) {
  const ref = useRef<THREE.Mesh>(null)

  const colors = [
    '#38bdf8',
    '#60a5fa',
    '#7dd3fc',
    '#3b82f6',
    '#38bdf8',
    '#818cf8',
  ]

  const color = accent || colors[index % colors.length]

  useFrame(({ clock }) => {
    const time = clock.getElapsedTime()

    if (!ref.current) return

    ref.current.position.y =
      Math.sin(time * 0.5 + index) * 0.12

    ref.current.position.x =
      Math.cos(time * 0.3 + index) * 0.04

    ref.current.rotation.y =
      time * 0.35 + index

    ref.current.rotation.x =
      time * 0.18
  })

  return (
    <mesh ref={ref}>
      <icosahedronGeometry args={[0.48, 1]} />

      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.35}
        metalness={0.85}
        roughness={0.12}
      />

      <pointLight
        color={color}
        intensity={1.2}
        distance={2.5}
      />
    </mesh>
  )
}

function SummitSolutionOrb({
  index,
  accent,
}: {
  index: number
  accent?: string
}) {
  return (
    <Canvas
      camera={{
        position: [0, 0, 2],
        fov: 50,
      }}
      gl={{
        antialias: true,
        alpha: true,
      }}
      dpr={[1, 1.5]}
      style={{
        width: '100%',
        height: '100%',
        background: 'transparent',
      }}
    >
      <ambientLight intensity={0.28} />

      <directionalLight
        position={[2, 2, 3]}
        intensity={1}
      />

      <SummitFloatingOrb index={index} accent={accent} />
    </Canvas>
  )
}

function SummitSolutionCard({
  solution,
  index,
  isVisible,
  selectedSolution,
  setSelectedSolution,
}: {
  solution: (typeof SOLUTIONS)[number]
  index: number
  isVisible: boolean
  selectedSolution: string | null
  setSelectedSolution: (id: string | null) => void
}) {
  const [hovered, setHovered] = useState(false)
  const isSelected = selectedSolution === solution.id
  const hasSelection = selectedSolution !== null

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={
        isVisible
          ? {
            opacity: 1,
            y: 0,
          }
          : {}
      }
      transition={{
        delay: 0.15 + index * 0.08,
        duration: 0.7,
        ease: [0.16, 1, 0.3, 1],
      }}
      style={{
        perspective: 1000,
        height: '100%',
      }}
    >
      <motion.div
        layoutId={`card-container-${solution.id}`}
        onMouseEnter={() => !hasSelection && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => {
          if (!hasSelection) {
            setSelectedSolution(solution.id)
          }
        }}
        animate={{
          rotateX: hovered && !hasSelection ? -3 : 0,
          rotateY: hovered && !hasSelection ? 3 : 0,
          y: hovered && !hasSelection ? -5 : 0,
          borderColor: hovered && !hasSelection
            ? `${solution.accent}55`
            : 'rgba(200,230,255,0.10)',
          opacity: hasSelection && !isSelected ? 0.25 : 1,
          scale: hasSelection && !isSelected ? 0.96 : 1,
        }}
        transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="summit-solution-card"
        style={{
          position: 'relative',
          overflow: 'hidden',
          cursor: hasSelection ? 'default' : 'pointer',
          transformStyle: 'preserve-3d',
          background:
            'linear-gradient(145deg, rgba(255,255,255,0.055), rgba(255,255,255,0.018))',
          border:
            '1px solid rgba(200,230,255,0.10)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          clipPath:
            'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
          boxShadow: hovered && !hasSelection
            ? `0 24px 60px rgba(0,0,0,0.34), 0 0 40px ${solution.accent}1f`
            : '0 12px 35px rgba(0,0,0,0.16)',
          height: '100%',
        }}
      >
        {/* Comet glow */}
        <motion.div
          animate={{
            opacity: hovered && !hasSelection ? 1 : 0,
            x: hovered && !hasSelection ? 0 : 40,
          }}
          transition={{
            duration: 0.45,
          }}
          style={{
            position: 'absolute',
            top: -80,
            right: -70,
            width: 240,
            height: 240,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${solution.accent}2e 0%, ${solution.accent}14 32%, transparent 70%)`,
            filter: 'blur(18px)',
            pointerEvents: 'none',
          }}
        />

        {/* Moving highlight */}
        <motion.div
          animate={{
            x: hovered && !hasSelection ? '130%' : '-120%',
          }}
          transition={{
            duration: hovered && !hasSelection ? 1.1 : 0,
            ease: 'easeInOut',
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: '-35%',
            width: '25%',
            height: '100%',
            transform: 'skewX(-18deg)',
            background:
              'linear-gradient(90deg, transparent, rgba(125,211,252,0.10), transparent)',
            pointerEvents: 'none',
          }}
        />

        {/* Top accent */}
        <motion.div
          animate={{
            scaleX: hovered && !hasSelection ? 1 : 0.55,
          }}
          transition={{ duration: 0.35 }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 2,
            transformOrigin: 'left',
            background: `linear-gradient(90deg, ${solution.accent}, #60a5fa, #8b5cf6)`,
          }}
        />

        {/* Orb surrounding glow */}
        <div
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${solution.accent}29, transparent 68%)`,
            filter: 'blur(10px)',
            pointerEvents: 'none',
          }}
        />

        {/* Existing 3D orb */}
        <motion.div
          layoutId={`card-orb-container-${solution.id}`}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 92,
            height: 92,
            opacity: hovered && !hasSelection ? 1 : 0.72,
            transform: 'translateZ(35px)',
            transition: 'opacity 0.3s ease',
          }}
        >
          <SummitSolutionOrb index={index} accent={solution.accent} />
        </motion.div>

        {/* Content */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
            zIndex: 2,
            padding: '24px 22px',
          }}
        >
          {/* Header Icon */}
          <motion.div
            layoutId={`card-icon-${solution.id}`}
            style={{
              width: 42,
              height: 42,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background:
                'linear-gradient(135deg, rgba(37,99,235,0.16), rgba(124,58,237,0.14))',
              border: `1px solid ${solution.accent}2e`,
              color: ACCENT_ICE,
              fontSize: 20,
              marginBottom: 16,
            }}
          >
            {solution.icon}
          </motion.div>

          {/* Title & Tag */}
          <div>
            <motion.h3
              layoutId={`card-title-${solution.id}`}
              style={{
                margin: 0,
                fontFamily: FONT_DISPLAY,
                fontSize: 'clamp(20px, 2vw, 27px)',
                lineHeight: 1.1,
                letterSpacing: '0.035em',
                color: WHITE,
                textTransform: 'uppercase',
              }}
            >
              {solution.label}
            </motion.h3>

            <motion.div
              layoutId={`card-tag-${solution.id}`}
              style={{
                marginTop: 7,
                fontFamily: FONT_MONO,
                fontSize: 8,
                lineHeight: 1.5,
                letterSpacing: '0.11em',
                color: ACCENT_ICE,
                opacity: 0.72,
              }}
            >
              {solution.tag}
            </motion.div>
          </div>

          <motion.p
            layoutId={`card-desc-${solution.id}`}
            style={{
              margin: '18px 0 0',
              fontFamily: FONT_BODY,
              fontSize: 13,
              lineHeight: 1.75,
              color: 'rgba(255,255,255,0.50)',
              transform: 'translateZ(18px)',
            }}
          >
            {solution.desc}
          </motion.p>

          <div
            style={{
              marginTop: 'auto',
              paddingTop: 18,
              borderTop: hovered && !hasSelection
                ? `1px solid ${solution.accent}24`
                : '1px solid rgba(200,230,255,0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: FONT_MONO,
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: '0.15em',
              color: hovered && !hasSelection
                ? ACCENT_ICE
                : 'rgba(200,230,255,0.45)',
              textTransform: 'uppercase',
            }}
          >
            EXPLORE →
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

function SolutionsEcosystemSection() {
  const [isVisible, setIsVisible] = useState(false)
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setSelectedSolution(null)
      }
    }

    if (selectedSolution) {
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', onKeyDown)
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [selectedSolution])

  return (
    <section
      id="ai-solutions"
      ref={ref}
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        background: DEEP,
        color: WHITE,
        padding:
          'clamp(90px, 10vw, 140px) 20px',
        zIndex: 250,
      }}
    >
      {/* Ambient background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: 'min(850px, 80vw)',
            height: 450,
            top: '28%',
            left: '50%',
            transform: 'translateX(-50%)',
            borderRadius: '50%',
            background:
              'radial-gradient(ellipse, rgba(56,189,248,0.05) 0%, rgba(96,165,250,0.025) 35%, transparent 72%)',
            filter: 'blur(70px)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.025,
            backgroundImage: `
              linear-gradient(rgba(125,211,252,1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(125,211,252,1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Heading */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 950,
          margin: '0 auto 70px',
          textAlign: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            color: ACCENT_ICE,
            textTransform: 'uppercase',
          }}
        >
          ◆ &nbsp; 05 / AI SOLUTIONS ECOSYSTEM
        </motion.div>

        <motion.h2
          initial={{
            opacity: 0,
            y: 30,
          }}
          animate={
            isVisible
              ? {
                opacity: 1,
                y: 0,
              }
              : {}
          }
          transition={{
            delay: 0.15,
            duration: 0.7,
          }}
          style={{
            margin: '18px 0 0',
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(48px, 7vw, 88px)',
            lineHeight: 0.92,
            letterSpacing: '0.03em',
            color: WHITE,
          }}
        >
          INTELLIGENT SOLUTIONS
          <br />
          <span
            style={{
              background:
                'linear-gradient(90deg, #38bdf8 0%, #60a5fa 50%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            FOR MODERN ENTERPRISES
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          style={{
            maxWidth: 680,
            margin: '20px auto 0',
            fontFamily: FONT_BODY,
            fontSize: 'clamp(13px, 1.4vw, 16px)',
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.46)',
          }}
        >
          Designed to automate communication, streamline
          operations, and accelerate business growth.
          Click any solution to explore capabilities.
        </motion.p>
      </div>

      {/* Cards */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: 1280,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          gap: 20,
        }}
      >
        {SOLUTIONS.map((solution, index) => (
          <SummitSolutionCard
            key={solution.id}
            solution={solution}
            index={index}
            isVisible={isVisible}
            selectedSolution={selectedSolution}
            setSelectedSolution={setSelectedSolution}
          />
        ))}
      </div>

      {/* Expanded Modal Overlay */}
      <AnimatePresence>
        {selectedSolution && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 500,
              padding: 20,
            }}
          >
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedSolution(null)}
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(3, 8, 15, 0.72)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
            />

            {/* Expanded Card Container */}
            {(() => {
              const solution = SOLUTIONS.find(s => s.id === selectedSolution);
              if (!solution) return null;
              const solutionIndex = SOLUTIONS.findIndex(s => s.id === selectedSolution);
              return (
                <motion.div
                  layoutId={`card-container-${solution.id}`}
                  transition={{
                    type: 'spring',
                    stiffness: 280,
                    damping: 28,
                  }}
                  style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 640,
                    maxHeight: 'min(90vh, 850px)',
                    overflowY: 'auto' as const,
                    background: 'linear-gradient(145deg, rgba(16, 24, 38, 0.95), rgba(8, 11, 12, 0.90))',
                    border: '1px solid rgba(200,230,255,0.15)',
                    backdropFilter: 'blur(24px)',
                    WebkitBackdropFilter: 'blur(24px)',
                    clipPath: 'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
                    boxShadow: `0 30px 70px rgba(0,0,0,0.6), 0 0 50px ${solution.accent}2b`,
                    zIndex: 501,
                    cursor: 'default',
                  }}
                >
                  {/* Close button X */}
                  <button
                    onClick={() => setSelectedSolution(null)}
                    style={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: WHITE,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 10,
                      transition: 'background 0.2s, color 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    }}
                  >
                    <X size={16} />
                  </button>

                  {/* Content Container */}
                  <div style={{ padding: '40px 36px', position: 'relative', zIndex: 2 }}>
                    {/* Header section */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 24, paddingRight: 40 }}>
                      {/* Icon */}
                      <motion.div
                        layoutId={`card-icon-${solution.id}`}
                        style={{
                          width: 54,
                          height: 54,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          background: 'linear-gradient(135deg, rgba(37,99,235,0.16), rgba(124,58,237,0.14))',
                          border: `1px solid ${solution.accent}3d`,
                          color: ACCENT_ICE,
                          fontSize: 24,
                        }}
                      >
                        {solution.icon}
                      </motion.div>

                      <div>
                        <motion.h3
                          layoutId={`card-title-${solution.id}`}
                          style={{
                            margin: 0,
                            fontFamily: FONT_DISPLAY,
                            fontSize: 'clamp(24px, 3vw, 36px)',
                            lineHeight: 1.1,
                            letterSpacing: '0.04em',
                            color: WHITE,
                            textTransform: 'uppercase',
                          }}
                        >
                          {solution.label}
                        </motion.h3>
                        <motion.div
                          layoutId={`card-tag-${solution.id}`}
                          style={{
                            marginTop: 8,
                            fontFamily: FONT_MONO,
                            fontSize: 10,
                            lineHeight: 1.5,
                            letterSpacing: '0.12em',
                            color: ACCENT_ICE,
                            opacity: 0.8,
                          }}
                        >
                          {solution.tag}
                        </motion.div>
                      </div>
                    </div>

                    {/* Description */}
                    <motion.p
                      layoutId={`card-desc-${solution.id}`}
                      style={{
                        margin: '0 0 32px',
                        fontFamily: FONT_BODY,
                        fontSize: 15,
                        lineHeight: 1.8,
                        color: 'rgba(255,255,255,0.65)',
                      }}
                    >
                      {solution.desc}
                    </motion.p>

                    {/* Divider */}
                    <div style={{ height: 1, background: 'rgba(200,230,255,0.12)', marginBottom: 28 }} />

                    {/* Capabilities / Points */}
                    <div style={{ marginBottom: 32 }}>
                      <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.15em', color: ACCENT_ICE, opacity: 0.6, textTransform: 'uppercase', marginBottom: 16 }}>
                        Capabilities
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                        {solution.points.map((point, pIdx) => (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + pIdx * 0.05 }}
                            key={point}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 12,
                            }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                marginTop: 8,
                                flexShrink: 0,
                                borderRadius: '50%',
                                background: solution.accent,
                                boxShadow: `0 0 10px ${solution.accent}ff`,
                              }}
                            />
                            <span
                              style={{
                                fontFamily: FONT_BODY,
                                fontSize: 13,
                                lineHeight: 1.5,
                                color: 'rgba(255,255,255,0.75)',
                              }}
                            >
                              {point}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{ height: 1, background: 'rgba(200,230,255,0.12)', marginBottom: 28 }} />

                    {/* Action Button: EXPLORE */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        style={{
                          background: solution.accent,
                          color: DARK,
                          border: 'none',
                          padding: '12px 28px',
                          fontFamily: FONT_MONO,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          clipPath: 'polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%)',
                          boxShadow: `0 10px 25px ${solution.accent}33`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        Explore {solution.label} <span style={{ fontSize: 12 }}>→</span>
                      </motion.button>
                    </div>
                  </div>

                  {/* Orb on the background for styling */}
                  <motion.div
                    layoutId={`card-orb-container-${solution.id}`}
                    style={{
                      position: 'absolute',
                      bottom: -20,
                      left: -20,
                      width: 160,
                      height: 160,
                      opacity: 0.5,
                      pointerEvents: 'none',
                    }}
                  >
                    <SummitSolutionOrb index={solutionIndex} accent={solution.accent} />
                  </motion.div>
                </motion.div>
              );
            })()}
          </div>
        )}
      </AnimatePresence>

      {/* Bottom accent */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{
          once: true,
          amount: 0.4,
        }}
        transition={{
          duration: 1.1,
          ease: [0.16, 1, 0.3, 1],
        }}
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          width: 'min(360px, 60vw)',
          height: 1,
          transform: 'translateX(-50%)',
          background:
            'linear-gradient(90deg, transparent, rgba(56,189,248,0.7), transparent)',
        }}
      />
    </section>
  )
}

const HOW_IT_WORKS_STEPS = [
  {
    num: '01',
    icon: '◎',
    label: 'Listen',
    color: '#38bdf8',
    desc:
      'Capture voice, text, documents, and real-time data from any channel — phone, web, mobile, or API.',
  },
  {
    num: '02',
    icon: '◈',
    label: 'Understand',
    color: '#60a5fa',
    desc:
      'Apply speech recognition, multilingual translation, and AI-powered language understanding to extract meaning.',
  },
  {
    num: '03',
    icon: '◇',
    label: 'Decide',
    color: '#7dd3fc',
    desc:
      'Analyze information, apply business logic, and generate intelligent recommendations using trained AI models.',
  },
  {
    num: '04',
    icon: '◉',
    label: 'Automate',
    color: '#38bdf8',
    desc:
      'Trigger workflows, integrate with existing systems, and perform actions automatically without human intervention.',
  },
  {
    num: '05',
    icon: '◆',
    label: 'Deliver Results',
    color: '#60a5fa',
    desc:
      'Improve response times, reduce operational costs, and accelerate business growth with measurable outcomes.',
  },
]

function HowItWorksStep({
  step,
  index,
  activeIndex,
  setActiveIndex,
}: {
  step: (typeof HOW_IT_WORKS_STEPS)[number]
  index: number
  activeIndex: number
  setActiveIndex: (index: number) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  const isLeft = index % 2 === 0

  const isActive = index === activeIndex
  const isPast = index < activeIndex
  const isFuture = index > activeIndex

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActiveIndex(index)
        }
      },
      {
        rootMargin: "-28% 0px -28% 0px",
        threshold: 0.15,
      }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [index, setActiveIndex])

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        minHeight: 220,
        display: 'flex',
        alignItems: 'center',
        padding: '20px 0',
      }}
    >
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            style={{
              position: 'absolute',
              inset: '0 0 0 0',
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 0,
              borderRadius: 24,
              filter: 'brightness(1.35)',
              maskImage:
                'radial-gradient(ellipse at center, black 20%, transparent 88%)',
              WebkitMaskImage:
                'radial-gradient(ellipse at center, black 20%, transparent 88%)',
            }}
          >
            <CanvasRevealEffect
              animationSpeed={0.45}
              containerClassName="bg-[#101826]"
              colors={[
                [56, 189, 248],
                [96, 165, 250],
                [125, 211, 252],
              ]}
              opacities={[
                0.15,
                0.18,
                0.22,
                0.28,
                0.35,
              ]}
              dotSize={2}
              showGradient={false}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Center node */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 20,
        }}
      >
        <motion.div
          animate={
            isActive || hovered
              ? {
                scale: 1.45,
                borderColor: step.color,
                backgroundColor: step.color,
                boxShadow: `0 0 24px ${step.color}`,
              }
              : isPast
              ? {
                scale: 1.0,
                borderColor: 'rgba(56, 189, 248, 0.45)',
                backgroundColor: '#080b0c',
                boxShadow: '0 0 0px rgba(0,0,0,0)',
              }
              : {
                scale: 0.8,
                borderColor: 'rgba(255,255,255,0.12)',
                backgroundColor: '#080b0c',
                boxShadow: '0 0 0px rgba(0,0,0,0)',
              }
          }
          transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1],
          }}
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '2px solid #7dd3fc',
          }}
        />
      </div>

      {/* Step content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '44%',
          marginLeft: isLeft ? 0 : '56%',
          padding: isLeft
            ? '0 52px 0 0'
            : '0 0 0 52px',
          textAlign: isLeft
            ? 'right'
            : 'left',
        }}
      >
        <motion.div
          animate={
            isActive
              ? {
                scale: 1.04,
                opacity: 1,
                x: 0,
              }
              : isPast
              ? {
                scale: 0.98,
                opacity: 0.65,
                x: 0,
              }
              : {
                scale: 0.95,
                opacity: 0.28,
                x: 0,
              }
          }
          transition={{
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent:
                isLeft
                  ? 'flex-end'
                  : 'flex-start',
              gap: 10,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: 9,
                letterSpacing: '0.18em',
                color: isActive ? 'rgba(125,211,252,0.9)' : isPast ? 'rgba(125,211,252,0.5)' : 'rgba(125,211,252,0.2)',
                transition: 'color 0.35s ease',
              }}
            >
              {step.num}
            </span>

            <span
              style={{
                fontSize: 20,
                color: isActive ? step.color : isPast ? 'rgba(56, 189, 248, 0.45)' : 'rgba(125,211,252,0.18)',
                textShadow: isActive ? `0 0 12px ${step.color}66` : 'none',
                transition: 'color 0.35s ease, text-shadow 0.35s ease',
              }}
            >
              {step.icon}
            </span>
          </div>

          <h3
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(22px, 2.8vw, 34px)',
              lineHeight: 1,
              letterSpacing: '0.04em',
              color: isActive ? WHITE : isPast ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)',
              textTransform: 'uppercase',
              transition: 'color 0.35s ease',
            }}
          >
            {step.label}
          </h3>

          <p
            style={{
              margin: '12px 0 0',
              fontFamily: FONT_BODY,
              fontSize: 'clamp(12px, 1.2vw, 15px)',
              lineHeight: 1.7,
              color: isActive ? 'rgba(255,255,255,0.75)' : isPast ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.18)',
              transition: 'color 0.35s ease',
            }}
          >
            {step.desc}
          </p>
        </motion.div>
      </div>
    </div>
  )
}

function HowItWorksSection() {
  const [isVisible, setIsVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  // Scroll tracking for progress line
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  })
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 25,
    restDelta: 0.001,
  })

  return (
    <section
      id="intelligence"
      ref={ref}
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        background: DEEP,
        color: WHITE,
        padding: 'clamp(90px, 10vw, 150px) 20px',
        zIndex: 250,
      }}
    >
      {/* Background Velora isometric grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.14,
          backgroundImage: `
            radial-gradient(rgba(56, 189, 248, 0.15) 1.5px, transparent 1.5px),
            radial-gradient(rgba(96, 165, 250, 0.1) 1.5px, transparent 1.5px)
          `,
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 20px 20px',
          pointerEvents: 'none',
          maskImage: 'radial-gradient(ellipse at center, black, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black, transparent 80%)',
        }}
      />

      {/* Ambient glow */}
      <motion.div
        animate={{
          scale: [1, 1.08, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={{
          position: 'absolute',
          top: '18%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(700px, 75vw)',
          height: '420px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(56,189,248,0.08) 0%, rgba(96,165,250,0.04) 35%, transparent 72%)',
          filter: 'blur(70px)',
          pointerEvents: 'none',
        }}
      />

      {/* Center atmospheric glow */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '48%',
          transform: 'translate(-50%, -50%)',
          width: 'min(700px, 70vw)',
          height: 'min(700px, 70vw)',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(56,189,248,0.08) 0%, rgba(96,165,250,0.035) 35%, transparent 68%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Heading */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 900,
          margin: '0 auto 80px',
          textAlign: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            color: ACCENT_ICE,
            textTransform: 'uppercase',
          }}
        >
          ◆ &nbsp; 04 / HOW IT WORKS
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{
            delay: 0.15,
            duration: 0.7,
          }}
          style={{
            margin: '18px 0 0',
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(48px, 7vw, 88px)',
            lineHeight: 0.92,
            letterSpacing: '0.03em',
            color: WHITE,
          }}
        >
          HOW GLIEDAI
          <br />
          <span
            style={{
              background:
                'linear-gradient(90deg, #38bdf8 0%, #60a5fa 50%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            WORKS FOR YOU
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          style={{
            maxWidth: 600,
            margin: '20px auto 0',
            fontFamily: FONT_BODY,
            fontSize: 'clamp(13px, 1.4vw, 16px)',
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.46)',
          }}
        >
          A simple five-step process that takes your data from raw input to
          measurable business outcomes.
        </motion.p>
      </div>

      {/* Timeline */}
      <div
        ref={timelineRef}
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 1100,
          margin: '0 auto',
        }}
      >
        {/* Spine Background (Faint static dashed line) */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: 1,
            transform: 'translateX(-50%)',
            background:
              'linear-gradient(to bottom, transparent 0%, rgba(56,189,248,0.1) 8%, rgba(96,165,250,0.18) 50%, rgba(139,92,246,0.1) 92%, transparent 100%)',
            borderLeft: '1px dashed rgba(56,189,248,0.22)',
            zIndex: 5,
          }}
        />

        {/* Spine Active Progress Indicator */}
        <motion.div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: 3,
            transform: 'translateX(-50%)',
            background:
              'linear-gradient(to bottom, #38bdf8 0%, #60a5fa 50%, #8b5cf6 100%)',
            boxShadow: '0 0 10px rgba(56,189,248,0.5), 0 0 20px rgba(96,165,250,0.3)',
            scaleY,
            transformOrigin: 'top',
            zIndex: 6,
          }}
        />

        {HOW_IT_WORKS_STEPS.map((step, index) => (
          <HowItWorksStep
            key={step.num}
            step={step}
            index={index}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        ))}
      </div>

      {/* Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={
          isVisible
            ? {
              opacity: 1,
              y: 0,
            }
            : {}
        }
        transition={{
          delay: 0.8,
          duration: 0.7,
        }}
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 0,
          maxWidth: 800,
          margin: '70px auto 0',
          background: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(200,230,255,0.10)',
          backdropFilter: 'blur(18px)',
        }}
      >
        {[
          ['< 50ms', 'Response Latency'],
          ['99.9%', 'Uptime SLA'],
          ['40+', 'Languages'],
        ].map(([value, label], index) => (
          <div
            key={label}
            style={{
              flex: '1 1 180px',
              padding: '24px 20px',
              textAlign: 'center',
              borderRight:
                index < 2
                  ? '1px solid rgba(200,230,255,0.08)'
                  : 'none',
            }}
          >
            <div
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: 'clamp(28px, 4vw, 42px)',
                color: ACCENT_ICE,
                letterSpacing: '0.04em',
              }}
            >
              {value}
            </div>

            <div
              style={{
                marginTop: 5,
                fontFamily: FONT_MONO,
                fontSize: 9,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: STEEL,
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </motion.div>
    </section>
  )
}

// ── Next Section: Engineered For ─────────────────────────────────

function SummitSolutionsSection() {
  const industries = [
    "Healthcare & Life Sciences",
    "Financial Services & Fintech",
    "E-Commerce & Retail",
    "Logistics & Supply Chain",
    "SaaS & Enterprise",
    "Manufacturing & Industry 4.0",
    "Real Estate & PropTech",
    "Education & EdTech",
    "Legal & Compliance",
    "Automotive & Mobility",
  ]
  const [hoveredIndustry, setHoveredIndustry] = useState<number | null>(null)

  return (
    <section
      id="solutions"
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '420px',
        overflow: 'hidden',
        background: DEEP,
        color: WHITE,
        padding: 'clamp(70px, 9vw, 110px) 0',
        zIndex: 250,
      }}
    >
      <style>{`
        @keyframes summit-industry-marquee {
          0% {
            transform: translateX(0);
          }

          100% {
            transform: translateX(-50%);
          }
        }

        .summit-industry-track {
          display: flex;
          width: max-content;
          animation: summit-industry-marquee 40s linear infinite;
          will-change: transform;
        }

        .summit-industry-container:hover .summit-industry-track {
          animation-play-state: paused;
        }
      `}</style>

      {/* Background glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(800px, 90vw)',
            height: '300px',
            opacity: 0.16,
            background:
              'radial-gradient(ellipse, rgba(139,92,246,0.42) 0%, transparent 68%)',
            filter: 'blur(70px)',
          }}
        />
      </div>

      {/* Section heading */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          textAlign: 'center',
          marginBottom: 'clamp(28px, 4vw, 44px)',
          padding: '0 20px',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '7px 16px',
            borderRadius: 999,
            border: '1px solid rgba(139,92,246,0.28)',
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 4px 20px rgba(139,92,246,0.08)',
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#8b5cf6',
              boxShadow: '0 0 10px rgba(139,92,246,0.8)',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          />

          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#c4b5fd',
              fontWeight: 700,
            }}
          >
            02 / ENGINEERED FOR
          </span>
        </div>

        <h2
          style={{
            margin: '18px auto 0',
            maxWidth: 900,
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(42px, 7vw, 82px)',
            lineHeight: 0.95,
            letterSpacing: '0.03em',
            color: WHITE,
          }}
        >
          AI BUILT FOR EVERY INDUSTRY
        </h2>

        <p
          style={{
            margin: '18px auto 0',
            maxWidth: 650,
            fontFamily: FONT_BODY,
            fontSize: 'clamp(13px, 1.4vw, 16px)',
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.46)',
          }}
        >
          Intelligent platforms and AI agents engineered to adapt to
          the workflows, data, and demands of modern industries.
        </p>
      </motion.div>

      {/* Industries marquee */}
      <div
        className="summit-industry-container"
        style={{
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          padding: '18px 0 28px',
        }}
      >
        {/* Left fade */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 'clamp(70px, 14vw, 220px)',
            zIndex: 20,
            pointerEvents: 'none',
            background:
              'linear-gradient(to right, #101826 0%, rgba(16,24,38,0.95) 22%, rgba(16,24,38,0) 100%)',
          }}
        />

        {/* Right fade */}
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 'clamp(70px, 14vw, 220px)',
            zIndex: 20,
            pointerEvents: 'none',
            background:
              'linear-gradient(to left, #101826 0%, rgba(16,24,38,0.95) 22%, rgba(16,24,38,0) 100%)',
          }}
        />

        <div className="summit-industry-track">
          {[...industries, ...industries].map((industry, index) => {
            const isHovered = hoveredIndustry === index
            return (
              <motion.div
                key={`${industry}-${index}`}
                whileHover={{
                  scale: 1.06,
                  y: -4,
                }}
                onMouseEnter={() => setHoveredIndustry(index)}
                onMouseLeave={() => setHoveredIndustry(null)}
                transition={{
                  duration: 0.25,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  flexShrink: 0,
                  margin: '0 8px',
                  padding: '13px 22px',
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.045)',
                  border: isHovered
                    ? '1px solid rgba(125,211,252,0.42)'
                    : '1px solid rgba(200,230,255,0.12)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: isHovered
                    ? '0 10px 30px rgba(56,189,248,0.14), 0 0 20px rgba(56,189,248,0.08)'
                    : '0 6px 24px rgba(0,0,0,0.12)',
                  cursor: 'default',
                  opacity: hoveredIndustry === null || isHovered ? 1 : 0.38,
                  transition: 'border-color 0.2s, box-shadow 0.2s, opacity 0.2s',
                }}
              >
                <span
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 'clamp(12px, 1.3vw, 15px)',
                    fontWeight: 500,
                    letterSpacing: '0.025em',
                    whiteSpace: 'nowrap',
                    color: isHovered ? '#c8e6ff' : 'rgba(255,255,255,0.64)',
                    transition: 'color 0.2s',
                  }}
                >
                  {industry}
                </span>
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* Bottom accent */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{
          duration: 1.2,
          ease: [0.16, 1, 0.3, 1],
        }}
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          width: 'min(360px, 60vw)',
          height: 1,
          transform: 'translateX(-50%)',
          transformOrigin: 'center',
          background:
            'linear-gradient(90deg, transparent, rgba(139,92,246,0.7), transparent)',
        }}
      />
    </section>
  )
}

// ── Next Section: AI Ecosystem ─────────────────────────────────

function SummitNetworkSection() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.15 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <section
      ref={ref}
      id="network"
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100svh',
        overflow: 'hidden',
        background: DEEP,
        color: WHITE,
        padding: 'clamp(80px, 9vw, 120px) 0',
        zIndex: 250,
      }}
    >
      {/* Summit ambient background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: 'min(650px, 65vw)',
            height: 'min(650px, 65vw)',
            top: '-20%',
            left: '-12%',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(58,100,120,0.18) 0%, transparent 68%)',
            filter: 'blur(90px)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            width: 'min(650px, 65vw)',
            height: 'min(650px, 65vw)',
            right: '-12%',
            bottom: '-18%',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(96,165,250,0.12) 0%, transparent 68%)',
            filter: 'blur(100px)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at center, rgba(200,230,255,0.025), transparent 45%)',
          }}
        />
      </div>

      {/* Heading */}
      <div
        style={{
          position: 'relative',
          zIndex: 20,
          textAlign: 'center',
          padding: '0 20px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: ACCENT_ICE,
          }}
        >
          ◆ &nbsp; 03 / CONNECTED AI ECOSYSTEM
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{
            delay: 0.15,
            duration: 0.7,
            ease: [0.16, 1, 0.3, 1],
          }}
          style={{
            margin: '18px 0 20px',
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(48px, 7vw, 88px)',
            lineHeight: 0.9,
            letterSpacing: '0.03em',
            color: WHITE,
          }}
        >
          YOUR CONNECTED
          <br />
          <span
            style={{
              background:
                'linear-gradient(90deg, #7dd3fc 0%, #60a5fa 50%, #a78bfa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AI ECOSYSTEM
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{
            maxWidth: 680,
            margin: '0 auto',
            fontFamily: FONT_BODY,
            fontSize: 'clamp(13px, 1.4vw, 16px)',
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.48)',
          }}
        >
          The GLIEDAI platform intelligently orchestrates real-time AI
          capabilities across your entire enterprise architecture.
          Click any node to explore connections.
        </motion.p>
      </div>

      {/* ORIGINAL RADIAL LAYOUT */}
      <div
        style={{
          position: 'relative',
          zIndex: 20,
          width: '100%',
          marginTop: 28,
        }}
      >
        <RadialOrbitalTimeline
          timelineData={NETWORK_SERVICES}
        />
      </div>
    </section>
  )
}

// ── Default export ────────────────────────────────────────────

export default function HeroSummitPage() {
  return (
    <div style={{ background: WHITE, minHeight: '100vh', fontFamily: FONT_BODY }}>
      <Helmet>
        <title>GliedAI - AI Solutions and Services</title>

        <meta
          name="description"
          content="GliedAI provides AI-powered solutions and services for modern businesses."
        />

        <link
          rel="canonical"
          href="https://gliedai.com/"
        />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500&family=Space+Mono:wght@400;700&display=swap');
        .summit-stroke-h1 { -webkit-text-stroke: 2px #080b0c; }
        @keyframes summit-eyebrow-drift { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(2px); } }
        .summit-eyebrow-text { animation: summijt-eyebrow-drift 9s ease-in-out 3s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 0.45; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.15); } }

        .summit-solution-card {
          min-height: 0;
          height: 310px;
        }
        @media (max-width: 900px) {
          .summit-solution-card {
            height: 300px;
          }
        }
        @media (max-width: 600px) {
          .summit-solution-card {
            min-height: 290px;
            height: auto;
          }
        }

        .summit-industries-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        @media (max-width: 900px) {
          .summit-industries-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
        }
        @media (max-width: 600px) {
          .summit-industries-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          #industries {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
        }

        @media (max-width: 900px) {
          #impact > div:nth-of-type(3) > div:nth-of-type(3) {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 40px !important;
          }

          #contact > div:nth-of-type(3) > div:nth-of-type(2) {
            grid-template-columns: 1fr !important;
            gap: 50px !important;
          }
        }

        @media (max-width: 600px) {
          #impact > div:nth-of-type(3) > div:nth-of-type(3) {
            grid-template-columns: 1fr !important;
            gap: 35px !important;
          }
        }
      `}</style>
      <NavbarSummit />
      <HeroSummit />
      <SummitSolutionsSection />
      <SummitNetworkSection />
      <HowItWorksSection />
      <SolutionsEcosystemSection />
      <IndustriesSection />
      <ImpactSection />
      <ContactSection />
      <FloatingDock />
    </div>
  )
}
